import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Music, MousePointer, Hand, Plus, StickyNote, ArrowRight, Grid3x3,
  ZoomIn, ZoomOut, Maximize2, Edit2, Trash2, Clock, MessageSquare,
  Zap, Sparkles, ChevronDown, ChevronUp, GitBranch, Circle, Home, RotateCcw,
  X, Layout, Network, Leaf, Palette, Wand2, Upload, Link, FileAudio,
  PenTool, Users, Eye, ChevronRight, ChevronLeft, Play, SkipForward,
  AlertCircle, Check, BookOpen, Square, Copy, Folder, Timer,
  FileText, Mic, ExternalLink, Search, Hash, Type, Download, Settings,
  Activity, TrendingUp, Minus, Brain, Volume2, Music2,
  Target, Calendar, Bell, UserPlus, Send, HelpCircle
} from 'lucide-react';

// Theme & constants
import {
  APP_VERSION, APP_BUILD_DATE,
  TRIAL_DAYS, TRIAL_KEY, getTrialInfo, startTrial, getTrialEmail, saveTrialUser,
  EARTH, TYPOGRAPHY,
  CARD_COLORS, SECTION_COLORS, SECTION_TYPES,
  STATUS_COLORS, CANVAS_THEMES, STICKY_COLORS,
  CONNECTOR_COLORS, KEY_SIGNATURES, ZOOM_FACTOR,
  getSectionType, calculateDuration, calculateDurationSeconds, calculateBarsForDuration
} from '../theme/constants';

import { GENRE_TEMPLATES, TEMPLATE_COUNT, AI_KEYWORD_MAP, SECTION_FLOW } from '../theme/templates';

// Utilities
import { downloadMIDI } from '../lib/MidiExport';
import { ArrangementMockupEngine } from '../lib/ArrangementMockupEngine';
import { isLicensed } from '../lib/license';
import { recordSave, recordExport, shouldShowNudge, snooze as snoozeBackupNudge } from '../lib/backupNudge';
import { applyKeyToSections, applyTempoToSections } from '../lib/sectionSync';

// Analysis modules
import { analyzeLoop } from '../lib/AudioAnalyzer.js';
import { classifyGenre } from '../lib/GenreClassifier.js';
import { inferLoopRole, getLoopSummary } from '../lib/LoopRoleInference.js';
import { generateArrangement } from '../lib/ArrangementGenerator.js';
import { generateFullGuidance, getSectionGuidance } from '../lib/ProductionGuidance.js';
import { findMatchingReferences, generateReferenceArrangement, getAllReferences, generateArrangementFromSearch, mapITunesGenre, findCuratedMatch, generateStandaloneTrackAnalysis, generateRecreationGuide } from '../lib/ReferenceLibrary.js';

// Components
import { TrialBadge, TrialExpiredGate } from '../components/TrialBadge';
import { LicenseKeyInput } from '../components/LicenseKeyInput';
import { ManageLicense } from '../components/ManageLicense';
import { TrialEmailGate } from '../components/TrialEmailGate';
import { BackupNudge } from '../components/BackupNudge';
import { SmartGeneratorPanel } from '../components/SmartGeneratorPanel';
import { TrackAnalyzerDialog } from '../components/TrackAnalyzerDialog';
import { LoopAnalyzerPanel } from '../components/LoopAnalyzerPanel';
import { LyricistSketchpad } from '../components/LyricistSketchpad';
import { ReferenceTrackAnalyzer } from '../components/ReferenceTrackAnalyzer';
import { CollaborationPanel } from '../components/CollaborationPanel';
import { AISuggestionCallout } from '../components/AISuggestionCallout';
import { StudioLandingPage } from '../components/StudioLandingPage';
import { ConnectorToolbar } from '../components/ConnectorToolbar';
import { ChangeLogPanel } from '../components/ChangeLogPanel';
import { CommentsPanel } from '../components/CommentsPanel';
import { AccountabilityWidget } from '../components/AccountabilityWidget';
import { CoachingSidebar } from '../components/CoachingSidebar';

// ─── Section energy defaults by type ────────────────────────────────────────
const SECTION_ENERGY_MAP = {
  intro: 0.20, verse: 0.45, 'pre-chorus': 0.60, chorus: 0.85,
  hook: 0.80, bridge: 0.35, solo: 0.65, outro: 0.15,
  drop: 1.0, build: 0.65, breakdown: 0.28, default: 0.50,
};

function getGenreFamily(genre = '') {
  const g = genre.toLowerCase();
  if (/house|techno|drum|dnb|bass|trance|dubstep|hardstyle|edm|electro/.test(g)) return 'electronic';
  if (/hip.hop|trap|boom|drill|lo.fi/.test(g)) return 'hip-hop';
  if (/rock|punk|metal|hardcore/.test(g)) return 'rock';
  return 'pop';
}

/**
 * Attach default energy levels and production guidance to a list of raw
 * section objects that came from a template or generator without rich data.
 * Skips sections that already have energy set (e.g. from loop analyser).
 */
function enrichSectionsWithGuidance(sections, { genre = 'Pop', tempo = 120, key = 'C Major' } = {}) {
  const context = {
    genre, family: getGenreFamily(genre), bpm: tempo, key,
    loopRole: null, loopElements: [],
  };
  return sections.map(sec => {
    if (sec.energy != null && sec.productionGuidance) return sec; // already enriched
    const sType = sec.sectionType || getSectionType(sec.name || '');
    const energy = sec.energy != null ? sec.energy : (SECTION_ENERGY_MAP[sType] ?? 0.50);
    const guidance = getSectionGuidance({ type: sType, energy, label: sec.name }, context);
    return { ...sec, energy, productionGuidance: sec.productionGuidance || guidance };
  });
}

function StudioApp({ onBackToHome, initialTemplate, trialDaysLeft, trialExpired, onLicenseActivated }) {
  const [currentView, setCurrentView] = useState(initialTemplate ? 'canvas' : 'welcome');
  const [canvasTheme, setCanvasTheme] = useState('light');
  const [projectName, setProjectName] = useState(initialTemplate?.name || '');
  const [showNamingDialog, setShowNamingDialog] = useState(false);
  const [showLicenseDialog, setShowLicenseDialog] = useState(false);
  const [showManageLicense, setShowManageLicense] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(initialTemplate || null);
  const [tempProjectName, setTempProjectName] = useState('');
  const [quickStartInput, setQuickStartInput] = useState('');
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [openGenreDropdown, setOpenGenreDropdown] = useState(null);
  const [canvasObjects, setCanvasObjects] = useState([]);
  const [arrows, setArrows] = useState([]);
  const [selectedObjects, setSelectedObjects] = useState([]);
  const [selectedArrow, setSelectedArrow] = useState(null);
  const [arrowToolbarPos, setArrowToolbarPos] = useState(null);
  const [tool, setTool] = useState('select');
  // Lifted state for persistence (must be declared before zoom/pan which reference it)
  const [lyrics, setLyrics] = useState({});
  const [accountability, setAccountability] = useState({ deadline: '', goalSections: 0, nudgesEnabled: false, nudgeFrequency: 'daily', nudgeEmail: '', partnerEmail: '', partnerName: '', partnerInvited: false, shareProgress: true });
  const [canvasViewState, setCanvasViewState] = useState({ zoom: 1, pan: { x: 0, y: 0 }, theme: 'light' });
  const [zoom, setZoomRaw] = useState(canvasViewState.zoom || 1);
  const [pan, setPanRaw] = useState(canvasViewState.pan || { x: 0, y: 0 });
  const setZoom = useCallback((val) => { const v = typeof val === 'function' ? val(zoom) : val; setZoomRaw(v); setCanvasViewState(prev => ({ ...prev, zoom: v })); }, [zoom]);
  const setPan = useCallback((val) => { const v = typeof val === 'function' ? val(pan) : val; setPanRaw(v); setCanvasViewState(prev => ({ ...prev, pan: v })); }, [pan]);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [snapToGrid, setSnapToGrid] = useState(false);
  const [editingObject, setEditingObject] = useState(null);
  const [draggedObject, setDraggedObject] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [hasDragged, setHasDragged] = useState(false);
  const [selectedArrowColor, setSelectedArrowColor] = useState('stone');
  const [draggingConnection, setDraggingConnection] = useState(null);
  const [connectionEnd, setConnectionEnd] = useState(null);
  const [hoveredObject, setHoveredObject] = useState(null);
  const [multiDragOffsets, setMultiDragOffsets] = useState({});
  const multiDragOffsetsRef = useRef({});
  const selectedObjectsRef = useRef([]);
  const [isBoxSelecting, setIsBoxSelecting] = useState(false);
  const [boxSelectStart, setBoxSelectStart] = useState(null);
  const [boxSelectEnd, setBoxSelectEnd] = useState(null);
  const [showCollabPanel, setShowCollabPanel] = useState(false);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [showLyricist, setShowLyricist] = useState(false);
  const [showChangeLog, setShowChangeLog] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showAccountability, setShowAccountability] = useState(false);
  const [showCoaching, setShowCoaching] = useState(false);
  const [coachingState, setCoachingState] = useState({ completedSteps: {} });
  const [showRefAnalyzer, setShowRefAnalyzer] = useState(false);
  const [showAIGenerator, setShowAIGenerator] = useState(false);
  const [showLoopAnalyzer, setShowLoopAnalyzer] = useState(false);
  const [showTrackAnalyzer, setShowTrackAnalyzer] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showCoachingBanner, setShowCoachingBanner] = useState(true);
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const [changeLog, setChangeLog] = useState([]);
  const [comments, setComments] = useState([]);
  const [projectTempo, setProjectTempo] = useState(120);
  const [projectKey, setProjectKey] = useState('C Major');
  const [loopAudioUrl, setLoopAudioUrl] = useState(null);
  const loopAudioRef = useRef(null);
  const [isPlayingLoop, setIsPlayingLoop] = useState(false);
  const mockupEngineRef = useRef(null);
  const [mockupState, setMockupState] = useState('idle'); // idle, ready, loading, playing, paused
  const [mockupExpanded, setMockupExpanded] = useState(true);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [mockupProgress, setMockupProgress] = useState(0);
  const [mockupElapsed, setMockupElapsed] = useState(0);
  const [mockupCurrentSection, setMockupCurrentSection] = useState(null);
  const [saveStatus, setSaveStatus] = useState('saved');
  const [lastSaved, setLastSaved] = useState(null);
  const [showBackupNudge, setShowBackupNudge] = useState(false);
  const [projectId, setProjectId] = useState(() => initialTemplate?.id || Date.now().toString());
  const canvasRef = useRef(null);
  const cardClickedRef = useRef(false);
  const saveTimeoutRef = useRef(null);

  // Keep refs in sync for drag handlers (avoids stale closures)
  useEffect(() => { multiDragOffsetsRef.current = multiDragOffsets; }, [multiDragOffsets]);
  useEffect(() => { selectedObjectsRef.current = selectedObjects; }, [selectedObjects]);

  // ==================== AUTO-SAVE SYSTEM ====================

  // Save project to localStorage
  const saveProject = useCallback(() => {
    if (!projectName) return;

    const projectData = {
      id: projectId,
      name: projectName,
      tempo: projectTempo,
      key: projectKey,
      canvasObjects,
      arrows,
      comments,
      lyrics,
      accountability,
      coachingState,
      canvasViewState,
      changeLog: changeLog.slice(0, 50), // Keep last 50 changes
      lastModified: new Date().toISOString(),
      version: '2.0'
    };

    // Save to localStorage — guarded against corrupt data and quota overflow.
    let savedProjects = {};
    try {
      savedProjects = JSON.parse(localStorage.getItem('arrangementlab_projects') || '{}');
      if (typeof savedProjects !== 'object' || savedProjects === null) savedProjects = {};
    } catch {
      savedProjects = {}; // corrupt store — start fresh rather than crash autosave
    }
    savedProjects[projectId] = projectData;
    try {
      localStorage.setItem('arrangementlab_projects', JSON.stringify(savedProjects));
      setSaveStatus('saved');
      setLastSaved(new Date());
      recordSave();
      if (shouldShowNudge()) setShowBackupNudge(true);
    } catch (err) {
      // Quota exceeded or storage unavailable — surface it instead of dying silently.
      console.warn('Autosave failed (storage full or unavailable):', err);
      setSaveStatus('error');
    }
  }, [projectId, projectName, projectTempo, projectKey, canvasObjects, arrows, comments, changeLog, lyrics, accountability, coachingState, canvasViewState]);

  // Auto-save when project data changes (debounced)
  useEffect(() => {
    if (currentView !== 'canvas' || !projectName) return;

    setSaveStatus('unsaved');

    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Debounce save - wait 2 seconds after last change
    saveTimeoutRef.current = setTimeout(() => {
      setSaveStatus('saving');
      setTimeout(() => {
        saveProject();
      }, 300);
    }, 2000);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [canvasObjects, arrows, projectName, projectTempo, currentView, saveProject, lyrics, accountability, coachingState]);

  // Catches the days-since-last-export case for a returning user who hasn't
  // triggered a fresh autosave yet (the save-count path above only fires on edit).
  useEffect(() => {
    if (currentView === 'canvas' && shouldShowNudge()) setShowBackupNudge(true);
  }, [currentView]);

  // Export project as JSON file
  const exportProject = useCallback(() => {
    const projectData = {
      id: projectId,
      name: projectName,
      tempo: projectTempo,
      key: projectKey,
      canvasObjects,
      arrows,
      comments,
      lyrics,
      accountability,
      canvasViewState,
      changeLog: changeLog.slice(0, 50),
      exportedAt: new Date().toISOString(),
      version: '2.0',
      app: 'arrangementLab'
    };

    const blob = new Blob([JSON.stringify(projectData, null, 2)], { type: 'application/octet-stream' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${projectName || 'arrangement'}.arrlab`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    recordExport();
    setShowBackupNudge(false);
  }, [projectId, projectName, projectTempo, projectKey, canvasObjects, arrows, comments, lyrics, accountability, canvasViewState, changeLog]);

  // Import project from JSON file
  const importProject = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = JSON.parse(e.target.result);
        if (data.app !== 'arrangementLab' || !data.canvasObjects) {
          alert('Invalid project file. Please open an arrangementLab project (.arrlab).');
          return;
        }
        setProjectId(data.id || Date.now().toString());
        setProjectName(data.name || 'Imported Project');
        setProjectTempo(data.tempo || 120);
        setProjectKey(data.key || 'C Major');
        setCanvasObjects(data.canvasObjects || []);
        setArrows(data.arrows || []);
        setComments(data.comments || []);
        setChangeLog(data.changeLog || []);
        if (data.lyrics) setLyrics(data.lyrics);
        if (data.accountability) setAccountability(prev => ({ ...prev, ...data.accountability }));
        if (data.canvasViewState) setCanvasViewState(data.canvasViewState);
        setCurrentView('canvas');
        setSaveStatus('saved');
      } catch (err) {
        alert('Failed to load project file');
      }
    };
    reader.readAsText(file);
  }, []);

  // Get saved projects from localStorage
  const getSavedProjects = useCallback(() => {
    const saved = localStorage.getItem('arrangementlab_projects');
    if (!saved) return [];
    let projects;
    try { projects = JSON.parse(saved); } catch (err) { console.warn('Invalid projects data'); return []; }
    return Object.values(projects).sort((a, b) =>
      new Date(b.lastModified) - new Date(a.lastModified)
    );
  }, []);

  // Load a saved project
  const loadSavedProject = useCallback((project) => {
    resetMockup();
    setProjectId(project.id);
    setProjectName(project.name);
    setProjectTempo(project.tempo || 120);
    setProjectKey(project.key || 'C Major');
    setCanvasObjects(project.canvasObjects || []);
    setArrows(project.arrows || []);
    setComments(project.comments || []);
    setChangeLog(project.changeLog || []);
    if (project.lyrics) setLyrics(project.lyrics);
    else setLyrics({});
    if (project.accountability) setAccountability(prev => ({ ...prev, ...project.accountability }));
    else setAccountability({ deadline: '', goalSections: 0, nudgesEnabled: false, nudgeFrequency: 'daily', nudgeEmail: '', partnerEmail: '', partnerName: '', partnerInvited: false, shareProgress: true });
    if (project.coachingState) setCoachingState(project.coachingState);
    else setCoachingState({ completedSteps: {} });
    if (project.canvasViewState) setCanvasViewState(project.canvasViewState);
    setCurrentView('canvas');
    setSaveStatus('saved');
    setLastSaved(new Date(project.lastModified));
  }, []);

  // Delete a saved project
  const deleteSavedProject = useCallback((projectIdToDelete) => {
    const saved = JSON.parse(localStorage.getItem('arrangementlab_projects') || '{}');
    delete saved[projectIdToDelete];
    localStorage.setItem('arrangementlab_projects', JSON.stringify(saved));
  }, []);

  const logChange = useCallback((a) => setChangeLog(p => [{ id: Date.now(), user: 'You', action: a, timestamp: new Date().toLocaleTimeString() }, ...p].slice(0, 50)), []);

  const addComment = (text) => {
    setComments(p => [...p, { id: Date.now(), user: 'You', text, timestamp: new Date().toLocaleTimeString() }]);
  };

  // Arrow/connector management - must be defined before useEffect that uses them
  const deleteSelectedArrow = useCallback((arrowToDelete) => {
    // Use passed arrow or fall back to selectedArrow (for keyboard delete)
    const arrow = arrowToDelete || selectedArrow;
    if (arrow && arrow.id) {
      setArrows(prevArrows => prevArrows.filter(a => a.id !== arrow.id));
      logChange('Deleted connection');
      setSelectedArrow(null);
      setArrowToolbarPos(null);
    }
  }, [selectedArrow, logChange]);

  const changeArrowColor = useCallback((colorId) => {
    if (selectedArrow) {
      setArrows(p => p.map(a => a.id === selectedArrow.id ? { ...a, color: colorId } : a));
      setSelectedArrow(prev => ({ ...prev, color: colorId }));
      logChange('Changed connection color');
    }
  }, [selectedArrow, logChange]);

  const handleArrowClick = (arrow, e) => {
    e.stopPropagation();
    setSelectedArrow(arrow);
    setSelectedObjects([]);
    const rect = canvasRef.current.getBoundingClientRect();
    setArrowToolbarPos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };

  const deleteSelected = useCallback(() => {
    if (selectedObjects.length === 0) return;
    setCanvasObjects(p => p.filter(o => !selectedObjects.includes(o.id)));
    setArrows(p => p.filter(a => !selectedObjects.includes(a.from) && !selectedObjects.includes(a.to)));
    logChange('Deleted ' + selectedObjects.length + ' object(s)');
    setSelectedObjects([]);
  }, [selectedObjects, logChange]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.key === 'v' || e.key === 'V') { e.preventDefault(); setTool('select'); }
      else if (e.key === 'h' || e.key === 'H') { e.preventDefault(); setTool('hand'); }
      else if (e.key === 'C' && e.shiftKey) {
        e.preventDefault();
        setShowCoaching(prev => {
          if (!prev) { setShowChangeLog(false); setShowComments(false); setShowAccountability(false); }
          return !prev;
        });
      }
      else if (e.key === 'c' || e.key === 'C') { e.preventDefault(); setTool('connect'); }
      else if ((e.key === 'Delete' || e.key === 'Backspace') && (selectedObjects.length > 0 || selectedArrow)) {
        e.preventDefault();
        if (selectedArrow) {
          deleteSelectedArrow();
        } else {
          deleteSelected();
        }
      }
      else if (e.key === 'Escape') {
        setSelectedArrow(null);
        setArrowToolbarPos(null);
        setDraggingConnection(null);
        setConnectionEnd(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedObjects, selectedArrow, deleteSelectedArrow, deleteSelected]);

  useEffect(() => {
    if (initialTemplate && initialTemplate.sections && Array.isArray(initialTemplate.sections) && canvasObjects.length === 0) {
      const tempo = initialTemplate.tempo || 120;
      setProjectTempo(tempo);
      const rawObjs = initialTemplate.sections.map((name, i) => ({ id: Date.now() + i, type: 'section', name, bars: initialTemplate.bars[i] || 8, key: initialTemplate.key || 'C Major', tempo, instruments: '', status: 'not-started', sectionType: getSectionType(name), customColor: null, x: 50 + (i % 5) * 260, y: 50 + Math.floor(i / 5) * 280 }));
      const objs = enrichSectionsWithGuidance(rawObjs, { genre: initialTemplate.genre || 'Pop', tempo, key: initialTemplate.key || 'C Major' });
      setCanvasObjects(objs);
      setProjectName(initialTemplate.name);
      const newArrows = [];
      for (let i = 0; i < objs.length - 1; i++) { newArrows.push({ id: Date.now() + 1000 + i, from: objs[i].id, to: objs[i + 1].id, fromSide: 'right', toSide: 'left', color: 'stone' }); }
      setArrows(newArrows);
    }
  }, [initialTemplate]);

  useEffect(() => {
    if (quickStartInput.trim().length > 2) {
      const words = quickStartInput.toLowerCase().split(/\s+/);
      const matched = new Set();
      words.forEach(w => Object.entries(AI_KEYWORD_MAP).forEach(([k, g]) => { if (w.includes(k)) g.forEach(x => matched.add(x)); }));
      if (matched.size === 0) { matched.add('Folk'); matched.add('Rock'); }
      const sugg = [];
      matched.forEach(g => { if (GENRE_TEMPLATES[g]) GENRE_TEMPLATES[g].slice(0, 2).forEach(t => sugg.push({ genre: g, ...t })); });
      setAiSuggestions(sugg.slice(0, 4));
    } else setAiSuggestions([]);
  }, [quickStartInput]);

  // SPRINT 4: Smoother zoom sensitivity — use setZoomRaw to avoid stale closure
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || currentView !== 'canvas') return;
    const h = (e) => {
      e.preventDefault();
      // Reduced zoom sensitivity: 3% per tick instead of 10%
      const factor = e.deltaY > 0 ? (1 - ZOOM_FACTOR) : (1 + ZOOM_FACTOR);
      setZoomRaw(prev => {
        const next = Math.min(3, Math.max(0.1, prev * factor));
        setCanvasViewState(s => ({ ...s, zoom: next }));
        return next;
      });
    };
    c.addEventListener('wheel', h, { passive: false });
    return () => c.removeEventListener('wheel', h);
  }, [currentView]);

  const handleSelectTemplate = (t) => { setSelectedTemplate(t); setTempProjectName(t.name + ' - ' + new Date().toLocaleDateString()); setShowNamingDialog(true); setOpenGenreDropdown(null); };
  const handleStartBlank = () => { setSelectedTemplate(null); setTempProjectName('Untitled - ' + new Date().toLocaleDateString()); setShowNamingDialog(true); };

  const handleConfirmProject = () => {
    setProjectName(tempProjectName || 'Untitled'); setShowNamingDialog(false);
    if (selectedTemplate) {
      const tempo = selectedTemplate.tempo || 120;
      setProjectTempo(tempo);
      const rawObjs = selectedTemplate.sections.map((name, i) => ({ id: Date.now() + i, type: 'section', name, bars: selectedTemplate.bars[i] || 8, key: projectKey, tempo, instruments: '', status: 'not-started', sectionType: getSectionType(name), customColor: null, x: 50 + (i % 5) * 260, y: 50 + Math.floor(i / 5) * 280 }));
      const objs = enrichSectionsWithGuidance(rawObjs, { genre: selectedTemplate.genre || selectedTemplate.name || 'Pop', tempo, key: projectKey });
      setCanvasObjects(objs);
      const newArrows = [];
      for (let i = 0; i < objs.length - 1; i++) { newArrows.push({ id: Date.now() + 1000 + i, from: objs[i].id, to: objs[i + 1].id, fromSide: 'right', toSide: 'left', color: 'stone' }); }
      setArrows(newArrows);
      logChange('Created project with ' + objs.length + ' sections');
    }
    setCurrentView('canvas');
  };

  const handleRefImport = (structure) => {
    const rawObjs = structure.sections.map((name, i) => ({ id: Date.now() + i, type: 'section', name, bars: structure.bars[i] || 8, key: projectKey, tempo: projectTempo, instruments: '', status: 'not-started', sectionType: getSectionType(name), customColor: null, x: 50 + (i % 5) * 260, y: 50 + Math.floor(i / 5) * 280 }));
    const objs = enrichSectionsWithGuidance(rawObjs, { genre: structure.genre || 'Pop', tempo: projectTempo, key: projectKey });
    setCanvasObjects(objs);
    setProjectName(structure.name || 'Reference Import');
    const newArrows = [];
    for (let i = 0; i < objs.length - 1; i++) { newArrows.push({ id: Date.now() + 1000 + i, from: objs[i].id, to: objs[i + 1].id, fromSide: 'right', toSide: 'left', color: 'stone' }); }
    setArrows(newArrows);
    logChange('Imported structure from reference track');
    setCurrentView('canvas');
    setShowRefAnalyzer(false);
  };

  const handleAIGenerate = (generated) => {
    setProjectTempo(generated.tempo);

    // Check if this came from the loop analyzer (has rich section data)
    const richSections = generated._richSections;
    const guidance = generated._guidance;

    // Use the key the analyzer detected so the section cards and the master Key
    // control agree from the first render. Previously the cards baked in the stale
    // projectKey (default C Major) while the header updated to the detected key a
    // few lines later, leaving the app showing two different keys at once.
    const detectedKey = (generated._analysis && generated._analysis.key) || projectKey;

    const objs = generated.sections.map((name, i) => {
      const base = {
        id: Date.now() + i, type: 'section', name, bars: generated.bars[i] || 8,
        key: detectedKey, tempo: generated.tempo, instruments: '', status: 'not-started',
        sectionType: getSectionType(name), customColor: null,
        x: 50 + (i % 5) * 260, y: 50 + Math.floor(i / 5) * 280
      };

      // Attach rich data from loop analyzer if available
      if (richSections && richSections[i]) {
        base.energy = richSections[i].energy;
        base.isLoopSection = richSections[i].isLoopSection;
        base.elements = richSections[i].elements;
        base.sectionGuidance = richSections[i].guidance;
      }
      if (guidance && guidance[i]) {
        base.productionGuidance = guidance[i].guidance;
      }

      return base;
    });

    // If no loop analyser data, auto-attach energy + production guidance from section names
    const finalObjs = richSections ? objs : enrichSectionsWithGuidance(objs, { genre: generated.genre, tempo: generated.tempo, key: detectedKey });
    setCanvasObjects(finalObjs);
    setProjectName(`${generated.genre} - ${new Date().toLocaleDateString()}`);
    const cols = 5;
    const newArrows = [];
    for (let i = 0; i < objs.length - 1; i++) {
      const fromRow = Math.floor(i / cols);
      const toRow = Math.floor((i + 1) / cols);
      if (fromRow === toRow) {
        newArrows.push({ id: Date.now() + 1000 + i, from: objs[i].id, to: objs[i + 1].id, fromSide: 'right', toSide: 'left', color: 'stone' });
      } else {
        newArrows.push({ id: Date.now() + 1000 + i, from: objs[i].id, to: objs[i + 1].id, fromSide: 'bottom', toSide: 'top', color: 'stone' });
      }
    }
    setArrows(newArrows);

    // Store analysis metadata at project level if available
    if (generated._analysis) {
      setProjectKey(detectedKey);
    }
    // Reset any prior mockup state before loading new loop
    resetMockup();
    // Store loop audio URL for playback (triggers 'ready' state via useEffect)
    if (generated._audioUrl) {
      setLoopAudioUrl(generated._audioUrl);
    }

    logChange(`Generated ${generated.sections.length} sections${richSections ? ' with production guidance' : ''}`);
    setCurrentView('canvas');
    setShowAIGenerator(false);
  };

  // SPRINT 4: MIDI Export handler
  const handleExportMIDI = () => {
    const sections = canvasObjects.filter(o => o.type === 'section').map(s => ({
      name: s.name,
      bars: s.bars,
      key: s.key,
      tempo: s.tempo || projectTempo
    }));
    if (sections.length === 0) {
      alert('Add some sections before exporting MIDI');
      return;
    }
    try {
      downloadMIDI(sections, projectName || 'Untitled', projectTempo);
      logChange('Exported MIDI file');
    } catch (err) {
      console.error('MIDI export failed:', err);
      alert('MIDI export failed. Please try again.');
    }
  };

  // ==================== ARRANGEMENT MOCKUP PREVIEW ====================
  const handleMockupPreview = async () => {
    if (!loopAudioUrl) return;
    const sections = canvasObjects.filter(o => o.type === 'section');
    if (sections.length === 0) return;

    // Stop loop player if playing
    if (loopAudioRef.current && isPlayingLoop) {
      loopAudioRef.current.pause();
      setIsPlayingLoop(false);
    }

    if (mockupState === 'playing') {
      mockupEngineRef.current?.pause();
      setMockupState('paused');
      return;
    }

    if (mockupState === 'paused') {
      mockupEngineRef.current?.play();
      setMockupState('playing');
      return;
    }

    // 'ready' falls through to initialize and play (same as idle)

    // Initialize engine
    setMockupState('loading');
    try {
      if (!mockupEngineRef.current) {
        mockupEngineRef.current = new ArrangementMockupEngine();
      }
      await mockupEngineRef.current.loadAudio(loopAudioUrl);
      mockupEngineRef.current.prepare(sections, projectTempo);
      mockupEngineRef.current.onProgress = (elapsed, progress, sectionInfo) => {
        setMockupElapsed(elapsed);
        setMockupProgress(progress);
        setMockupCurrentSection(sectionInfo ? sectionInfo.index : null);
      };
      mockupEngineRef.current.play();
      setMockupState('playing');
      logChange('Started arrangement mockup preview');
    } catch (err) {
      console.error('Mockup preview failed:', err);
      setMockupState('idle');
    }
  };

  const handleMockupStop = () => {
    mockupEngineRef.current?.stop();
    setMockupState('idle');
    setMockupProgress(0);
    setMockupElapsed(0);
    setMockupCurrentSection(null);
  };

  const handleMockupSeek = (e) => {
    const engine = mockupEngineRef.current;
    if (!engine) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    engine.seekTo(ratio * engine.totalDuration);
  };

  // Reset all mockup state (called when navigating home or starting a new project)
  const resetMockup = useCallback(() => {
    mockupEngineRef.current?.stop();
    mockupEngineRef.current = null;
    setMockupState('idle');
    setMockupProgress(0);
    setMockupElapsed(0);
    setMockupCurrentSection(null);
    setLoopAudioUrl(null);
    setIsPlayingLoop(false);
    setMockupExpanded(true);
  }, []);

  // Cleanup mockup engine on unmount
  useEffect(() => {
    return () => {
      if (mockupEngineRef.current) {
        mockupEngineRef.current.destroy();
        mockupEngineRef.current = null;
      }
    };
  }, []);

  // Show arrangement preview player (ready state) when a loop is loaded
  useEffect(() => {
    if (!loopAudioUrl) return;
    if (mockupState !== 'idle') return;
    setMockupState('ready');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loopAudioUrl]);

  const screenToCanvas = (x, y) => ({ x: (x - pan.x) / zoom, y: (y - pan.y) / zoom });
  const getObjectCenter = (objId, side = 'center') => {
    const obj = canvasObjects.find(o => o.id === objId);
    if (!obj) return { x: 0, y: 0 };
    const w = obj.type === 'section' ? 200 : 180, h = obj.type === 'section' ? 240 : 140;
    const pos = { center: { x: obj.x + w / 2, y: obj.y + h / 2 }, top: { x: obj.x + w / 2, y: obj.y }, right: { x: obj.x + w, y: obj.y + h / 2 }, bottom: { x: obj.x + w / 2, y: obj.y + h }, left: { x: obj.x, y: obj.y + h / 2 } };
    return pos[side] || pos.center;
  };
  const updateObject = (id, updates) => { setCanvasObjects(p => p.map(o => o.id === id ? { ...o, ...updates } : o)); };
  const lastSection = canvasObjects.filter(o => o.type === 'section').slice(-1)[0];

  const handleCanvasDoubleClick = (e) => {
    // Only trigger on empty canvas area (svg background), not on existing objects
    if (e.target !== canvasRef.current && !e.target.closest('svg')) return;
    const targetTag = e.target.tagName?.toLowerCase();
    if (targetTag === 'path' || targetTag === 'circle' || targetTag === 'foreignobject' || targetTag === 'rect') {
      // Check if it's the background grid rect (has pointer-events: none) — allow that
      if (targetTag !== 'rect' || e.target.style?.pointerEvents !== 'none') return;
    }
    const rect = canvasRef.current.getBoundingClientRect();
    const { x, y } = screenToCanvas(e.clientX - rect.left, e.clientY - rect.top);
    const id = Date.now();
    setCanvasObjects(p => [...p, {
      id,
      type: 'section',
      name: 'New Section',
      bars: 8,
      key: projectKey,
      tempo: projectTempo,
      instruments: '',
      status: 'not-started',
      sectionType: 'custom',
      customColor: null,
      x, y
    }]);
    logChange('Added section via double-click');
    setSelectedObjects([id]);
    setEditingObject(id);
  };

  const handleCanvasMouseDown = (e) => {
    // If a card handler already claimed this click, skip canvas handling
    if (cardClickedRef.current) { cardClickedRef.current = false; return; }

    if (e.target !== canvasRef.current && !e.target.closest('svg')) return;

    // Don't process if clicking on an arrow path (let arrow handler deal with it)
    const targetTag = e.target.tagName?.toLowerCase();
    if (targetTag === 'path') return;

    // Don't process if clicking on a connection handle circle
    if (targetTag === 'circle' && e.target.style?.cursor === 'crosshair') return;

    // Don't process if clicking inside a card group (rect inside a g with transform)
    if (targetTag === 'rect' && e.target.closest('g[transform]')) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const { x, y } = screenToCanvas(e.clientX - rect.left, e.clientY - rect.top);

    // Clear arrow selection and close edit mode when clicking canvas background
    setSelectedArrow(null);
    setArrowToolbarPos(null);
    setEditingObject(null);

    if (tool === 'hand' || e.button === 1) { setIsPanning(true); setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y }); return; }
    if (tool === 'select' || tool === 'connect') { if (!e.shiftKey) setSelectedObjects([]); setIsBoxSelecting(true); setBoxSelectStart({ x, y }); setBoxSelectEnd({ x, y }); }
  };

  const handleCanvasMouseMove = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const { x, y } = screenToCanvas(e.clientX - rect.left, e.clientY - rect.top);
    if (isPanning) { setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y }); return; }
    if (isBoxSelecting) { setBoxSelectEnd({ x, y }); return; }
    if (draggingConnection) { setConnectionEnd({ x, y }); return; }
    if (draggedObject) {
      setHasDragged(true);
      const selObjs = selectedObjectsRef.current;
      const offsets = multiDragOffsetsRef.current;
      if (selObjs.length > 1 && selObjs.includes(draggedObject)) {
        setCanvasObjects(prev => prev.map(o => { if (!selObjs.includes(o.id)) return o; const offset = offsets[o.id] || { x: 0, y: 0 }; let nx = x + offset.x, ny = y + offset.y; if (snapToGrid) { nx = Math.round(nx / 20) * 20; ny = Math.round(ny / 20) * 20; } return { ...o, x: nx, y: ny }; }));
      } else {
        setCanvasObjects(prev => prev.map(o => { if (o.id !== draggedObject) return o; let nx = x - dragOffset.x, ny = y - dragOffset.y; if (snapToGrid) { nx = Math.round(nx / 20) * 20; ny = Math.round(ny / 20) * 20; } return { ...o, x: nx, y: ny }; }));
      }
    }
  };

  const handleCanvasMouseUp = (e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const { x, y } = screenToCanvas(e.clientX - rect.left, e.clientY - rect.top);
    setIsPanning(false);
    if (draggingConnection && connectionEnd) {
      // Expanded snap area - check proximity to connection handles (40px radius) or inside object with margin
      const snapRadius = 40;
      let targetObj = null;
      let closestSide = 'left';
      let minDist = Infinity;

      canvasObjects.forEach(o => {
        if (o.id === draggingConnection.fromId) return;
        const w = o.type === 'section' ? 200 : 180;
        const h = o.type === 'section' ? 200 : 140;
        const cx = o.x + w / 2, cy = o.y + h / 2;
        // Connection handle positions
        const handles = {
          top: { x: cx, y: o.y },
          right: { x: o.x + w, y: cy },
          bottom: { x: cx, y: o.y + h },
          left: { x: o.x, y: cy }
        };
        // Check distance to each handle
        Object.entries(handles).forEach(([side, pos]) => {
          const dist = Math.hypot(x - pos.x, y - pos.y);
          if (dist < snapRadius && dist < minDist) {
            minDist = dist;
            targetObj = o;
            closestSide = side;
          }
        });
        // Also check if inside object bounds with margin
        const margin = 20;
        if (!targetObj && x >= o.x - margin && x <= o.x + w + margin && y >= o.y - margin && y <= o.y + h + margin) {
          const tc = { x: o.x + w / 2, y: o.y + h / 2 };
          ['top', 'right', 'bottom', 'left'].forEach(side => {
            const pos = handles[side];
            const dist = Math.hypot(x - pos.x, y - pos.y);
            if (dist < minDist) { minDist = dist; targetObj = o; closestSide = side; }
          });
        }
      });

      if (targetObj) {
        setArrows(p => [...p, { id: Date.now(), from: draggingConnection.fromId, to: targetObj.id, fromSide: draggingConnection.side, toSide: closestSide, color: selectedArrowColor }]);
        logChange('Connected sections');
      }
      setDraggingConnection(null); setConnectionEnd(null); return;
    }
    if (isBoxSelecting && boxSelectStart) {
      const minX = Math.min(boxSelectStart.x, x), maxX = Math.max(boxSelectStart.x, x);
      const minY = Math.min(boxSelectStart.y, y), maxY = Math.max(boxSelectStart.y, y);
      const selected = canvasObjects.filter(o => {
        const w = o.type === 'section' ? 200 : 180;
        const h = o.type === 'section' ? 240 : 140;
        return o.x >= minX && o.x + w <= maxX && o.y >= minY && o.y + h <= maxY;
      }).map(o => o.id);
      setSelectedObjects(prev => e.shiftKey ? [...new Set([...prev, ...selected])] : selected);
    }
    setIsBoxSelecting(false); setBoxSelectStart(null); setBoxSelectEnd(null);
    if (draggedObject && hasDragged) logChange('Moved object');
    setDraggedObject(null); setHasDragged(false);
  };

  const handleAddSuggestedSection = (name, bars) => {
    const sections = canvasObjects.filter(o => o.type === 'section');
    const lastSec = sections[sections.length - 1];
    const newX = lastSec ? lastSec.x + 260 : 50;
    const newY = lastSec ? lastSec.y : 50;
    const newId = Date.now();
    const newObj = { id: newId, type: 'section', name, bars, key: projectKey, tempo: projectTempo, instruments: '', status: 'not-started', sectionType: getSectionType(name), customColor: null, x: newX, y: newY };
    setCanvasObjects(p => [...p, newObj]);
    if (lastSec) { setArrows(p => [...p, { id: Date.now() + 1, from: lastSec.id, to: newId, fromSide: 'right', toSide: 'left', color: 'stone' }]); }
    logChange('Added ' + name + ' section');
  };

  const renderArrows = () => arrows.map(a => {
    const fromObj = canvasObjects.find(o => o.id === a.from);
    const toObj = canvasObjects.find(o => o.id === a.to);
    if (!fromObj || !toObj) return null;
    const start = getObjectCenter(a.from, a.fromSide);
    const end = getObjectCenter(a.to, a.toSide);
    const color = CONNECTOR_COLORS.find(c => c.id === a.color)?.color || EARTH.stone[500];
    const isSelected = selectedArrow?.id === a.id;

    // Smart routing - create orthogonal paths that go AROUND cards, not through them
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const minOffset = 150; // Minimum distance to extend before turning

    // Determine if this is a "difficult" diagonal connection
    const isHorizontalFrom = a.fromSide === 'left' || a.fromSide === 'right';
    const isHorizontalTo = a.toSide === 'left' || a.toSide === 'right';
    const isVerticalFrom = a.fromSide === 'top' || a.fromSide === 'bottom';
    const isVerticalTo = a.toSide === 'top' || a.toSide === 'bottom';

    let path;

    // For mixed orientations (horizontal to vertical or vice versa), use orthogonal routing
    if ((isHorizontalFrom && isVerticalTo) || (isVerticalFrom && isHorizontalTo)) {
      // Create an L-shaped or stepped path
      let midX, midY;

      if (isHorizontalFrom) {
        // Go out horizontally first, then vertically
        midX = a.fromSide === 'right' ? Math.max(start.x + minOffset, end.x + (a.toSide === 'top' || a.toSide === 'bottom' ? 0 : minOffset)) : Math.min(start.x - minOffset, end.x);
        midY = end.y + (a.toSide === 'top' ? -minOffset : (a.toSide === 'bottom' ? minOffset : 0));
        path = `M ${start.x} ${start.y} L ${midX} ${start.y} Q ${midX} ${end.y}, ${end.x} ${end.y}`;
      } else {
        // Go out vertically first, then horizontally
        midY = a.fromSide === 'bottom' ? Math.max(start.y + minOffset, end.y) : Math.min(start.y - minOffset, end.y);
        midX = end.x + (a.toSide === 'left' ? -minOffset : (a.toSide === 'right' ? minOffset : 0));
        path = `M ${start.x} ${start.y} L ${start.x} ${midY} Q ${end.x} ${midY}, ${end.x} ${end.y}`;
      }
    } else {
      // Same orientation - use smooth bezier with adaptive offset
      // Cap the offset to prevent loops when cards are close together or overlapping
      const dist = Math.hypot(dx, dy);
      let cp1x, cp1y, cp2x, cp2y;
      const offset = Math.min(Math.max(40, dist * 0.4), Math.max(minOffset, Math.abs(isHorizontalFrom ? dx : dy) * 0.6));

      if (a.fromSide === 'right') { cp1x = start.x + offset; cp1y = start.y; }
      else if (a.fromSide === 'left') { cp1x = start.x - offset; cp1y = start.y; }
      else if (a.fromSide === 'top') { cp1x = start.x; cp1y = start.y - offset; }
      else { cp1x = start.x; cp1y = start.y + offset; }

      if (a.toSide === 'left') { cp2x = end.x - offset; cp2y = end.y; }
      else if (a.toSide === 'right') { cp2x = end.x + offset; cp2y = end.y; }
      else if (a.toSide === 'top') { cp2x = end.x; cp2y = end.y - offset; }
      else { cp2x = end.x; cp2y = end.y + offset; }

      path = `M ${start.x} ${start.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${end.x} ${end.y}`;
    }

    return (
      <g key={a.id} style={{ cursor: 'pointer' }}>
        {/* Selection highlight - render first (behind) */}
        {isSelected && (
          <path
            d={path}
            stroke={EARTH.terracotta[200]}
            strokeWidth="8"
            fill="none"
            opacity="0.5"
            style={{ pointerEvents: 'none' }}
          />
        )}
        {/* Invisible wider path for easier clicking */}
        <path
          d={path}
          stroke="transparent"
          strokeWidth="24"
          fill="none"
          style={{ cursor: 'pointer', pointerEvents: 'stroke' }}
          onClick={(e) => { e.stopPropagation(); handleArrowClick(a, e); }}
          onMouseDown={(e) => e.stopPropagation()}
        />
        {/* Visible path */}
        <path
          d={path}
          stroke={isSelected ? EARTH.terracotta[500] : color}
          strokeWidth={isSelected ? 3 : 2}
          fill="none"
          markerEnd={`url(#arrowhead-${a.color || 'stone'})`}
          style={{ cursor: 'pointer', transition: 'stroke 0.2s, stroke-width 0.2s', pointerEvents: 'stroke' }}
          onClick={(e) => { e.stopPropagation(); handleArrowClick(a, e); }}
          onMouseDown={(e) => e.stopPropagation()}
        />
      </g>
    );
  });

  const ConnectionHandle = ({ objId, side, x, y, isHovered }) => (
    <g>
      {/* Larger invisible hit area */}
      <circle
        cx={x}
        cy={y}
        r={12}
        fill="transparent"
        style={{ cursor: 'crosshair' }}
        onMouseDown={(e) => {
          e.stopPropagation();
          setDraggingConnection({ fromId: objId, side });
          setConnectionEnd({ x, y });
        }}
      />
      {/* Visible handle */}
      <circle
        cx={x}
        cy={y}
        r={isHovered ? 8 : 6}
        fill={tool === 'connect' ? EARTH.terracotta[500] : EARTH.terracotta[400]}
        stroke="#fff"
        strokeWidth={2}
        style={{
          cursor: 'crosshair',
          transition: 'r 0.15s ease',
          filter: isHovered ? 'drop-shadow(0 0 4px rgba(226, 113, 77, 0.5))' : 'none'
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          setDraggingConnection({ fromId: objId, side });
          setConnectionEnd({ x, y });
        }}
      />
      {/* Plus icon on hover when in connect mode */}
      {isHovered && tool === 'connect' && (
        <>
          <line x1={x-3} y1={y} x2={x+3} y2={y} stroke="#fff" strokeWidth={2} style={{ pointerEvents: 'none' }} />
          <line x1={x} y1={y-3} x2={x} y2={y+3} stroke="#fff" strokeWidth={2} style={{ pointerEvents: 'none' }} />
        </>
      )}
    </g>
  );

  const totalBars = canvasObjects.filter(o => o.type === 'section').reduce((sum, s) => sum + (s.bars || 0), 0);
  const totalDuration = calculateDuration(totalBars, projectTempo);

  if (currentView === 'welcome') {
    const savedProjects = getSavedProjects();

    return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #faf8f5 0%, #f5f0e8 100%)' }}>
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Minimal top bar */}
        <div className="flex items-center justify-between mb-8" style={{ borderBottom: '1px solid rgba(214, 211, 209, 0.4)', paddingBottom: '16px' }}>
          <button onClick={onBackToHome} className="flex items-center gap-2 text-sm" style={{ color: EARTH.stone[500] }}><Home className="w-4 h-4" /> Home</button>
          <div className="flex gap-2">
            <label className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-lg cursor-pointer" style={{ backgroundColor: EARTH.stone[200], color: EARTH.stone[600] }}>
              <Upload className="w-3.5 h-3.5" />Open Project
              <input type="file" accept=".arrlab,.json" className="hidden" onChange={(e) => e.target.files[0] && importProject(e.target.files[0])} />
            </label>
          </div>
        </div>

        {/* Hero — The Promise */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-2 leading-tight" style={{ color: EARTH.stone[800], fontFamily: TYPOGRAPHY.display }}>
            You bring the idea. We'll map the arrangement.
          </h1>
          <p className="text-lg max-w-xl mx-auto" style={{ color: EARTH.stone[400] }}>
            Whether it's a beat you tapped out, a song you love, or just a vibe — we'll help you plan a full arrangement with step-by-step production guidance.
          </p>
        </div>

        {/* Three Hero Entry Points */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {/* Entry 1: I have a beat */}
          <button
            onClick={() => setShowLoopAnalyzer(true)}
            className="p-7 rounded-2xl text-left transition-all shadow-lg hover:-translate-y-1 group"
            style={{ background: `linear-gradient(135deg, ${EARTH.terracotta[500]}, ${EARTH.terracotta[600]})`, color: '#fff', transform: 'translateY(0)', transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
          >
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <Upload className="w-7 h-7" />
            </div>
            <div className="font-bold text-lg mb-2 tracking-wide" style={{ fontFamily: TYPOGRAPHY.display }}>I have a beat or loop</div>
            <p className="text-base opacity-90 leading-relaxed">Upload your audio — even a rough 8-bar sketch. We'll analyze it and suggest a full song structure around it, with tips for every section.</p>
          </button>

          {/* Entry 2: I love this song */}
          <button
            onClick={() => setShowTrackAnalyzer(true)}
            className="p-7 rounded-2xl text-left transition-all shadow-lg hover:-translate-y-1 group"
            style={{ background: `linear-gradient(135deg, ${EARTH.forest[600]}, ${EARTH.forest[700]})`, color: '#fff', transform: 'translateY(0)', transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
          >
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <Search className="w-7 h-7" />
            </div>
            <div className="font-bold text-lg mb-2 tracking-wide" style={{ fontFamily: TYPOGRAPHY.display }}>I love this song</div>
            <p className="text-base opacity-90 leading-relaxed">Search any published track. We'll reverse-engineer how it's built — the structure, the energy flow, and how to arrange something like it.</p>
          </button>

          {/* Entry 3: Just get me started */}
          <button
            onClick={() => setShowAIGenerator(true)}
            className="p-7 rounded-2xl text-left transition-all shadow-lg hover:-translate-y-1 group"
            style={{ background: `linear-gradient(135deg, ${EARTH.stone[600]}, ${EARTH.stone[700]})`, color: '#fff', transform: 'translateY(0)', transition: 'all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' }}
          >
            <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
              <Sparkles className="w-7 h-7" />
            </div>
            <div className="font-bold text-lg mb-2 tracking-wide" style={{ fontFamily: TYPOGRAPHY.display }}>Just get me started</div>
            <p className="text-base opacity-90 leading-relaxed">Tell us a genre or vibe and we'll lay out a complete song structure instantly. No audio needed — just pick a direction and go.</p>
          </button>
        </div>

        {/* How It Works — dead simple */}
        <div className="mb-10 p-6 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.7)', border: `1px solid ${EARTH.stone[200]}`, backdropFilter: 'blur(8px)' }}>
          <h2 className="text-sm font-bold mb-4 text-center" style={{ color: EARTH.stone[500], fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '1px' }}>How it works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-11 h-11 rounded-full flex items-center justify-center mx-auto mb-2 font-bold" style={{ backgroundColor: EARTH.terracotta[100], color: EARTH.terracotta[600] }}>1</div>
              <div className="font-bold text-base mb-1" style={{ color: EARTH.stone[800] }}>Start with anything</div>
              <p className="text-sm leading-relaxed" style={{ color: EARTH.stone[500] }}>A beat, a song you like, or just a genre. That's your starting point.</p>
            </div>
            <div>
              <div className="w-11 h-11 rounded-full flex items-center justify-center mx-auto mb-2 font-bold" style={{ backgroundColor: EARTH.olive[100], color: EARTH.olive[600] }}>2</div>
              <div className="font-bold text-base mb-1" style={{ color: EARTH.stone[800] }}>Get a song blueprint</div>
              <p className="text-sm leading-relaxed" style={{ color: EARTH.stone[500] }}>We lay out a complete structure — intro, verse, chorus, drop — visually on a canvas.</p>
            </div>
            <div>
              <div className="w-11 h-11 rounded-full flex items-center justify-center mx-auto mb-2 font-bold" style={{ backgroundColor: EARTH.sage[100], color: EARTH.sage[600] }}>3</div>
              <div className="font-bold text-base mb-1" style={{ color: EARTH.stone[800] }}>Follow the guide</div>
              <p className="text-sm leading-relaxed" style={{ color: EARTH.stone[500] }}>Each section comes with production tips — what sounds to use, how to mix it, and what to do in your DAW.</p>
            </div>
          </div>
        </div>

        {/* Recent Projects */}
        {savedProjects.length > 0 && (
          <div className="mb-8">
            <h2 className="text-sm font-bold mb-3" style={{ color: EARTH.stone[500], fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '1px' }}>Recent Projects</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {savedProjects.slice(0, 6).map(project => (
                <div key={project.id} className="p-3 rounded-xl cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md" style={{ backgroundColor: '#fff', border: '1px solid rgba(214, 211, 209, 0.5)' }} onClick={() => loadSavedProject(project)}>
                  <div className="flex items-start justify-between mb-1">
                    <h3 className="font-medium text-sm truncate" style={{ color: EARTH.stone[800] }}>{project.name}</h3>
                    <button onClick={(e) => { e.stopPropagation(); if (confirm('Delete this project?')) deleteSavedProject(project.id); }} className="p-1 rounded opacity-40 hover:opacity-100" style={{ color: EARTH.stone[400] }}><X className="w-3 h-3" /></button>
                  </div>
                  <div className="flex items-center gap-3 text-xs" style={{ color: EARTH.stone[500] }}>
                    <span>{project.canvasObjects?.filter(o => o.type === 'section').length || 0} sections</span>
                    <span>{project.tempo || 120} BPM</span>
                    <span>{new Date(project.lastModified).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Browse Templates — collapsed by default */}
        <div className="mb-8">
          <button
            onClick={() => setShowTemplates(!showTemplates)}
            className="flex items-center gap-2 text-base font-medium mb-3"
            style={{ color: EARTH.stone[500] }}
          >
            {showTemplates ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Browse {TEMPLATE_COUNT}+ genre templates
          </button>
          {showTemplates && (
            <>
              {/* Quick-start search */}
              <div className="max-w-md mb-4">
                <input type="text" value={quickStartInput} onChange={(e) => setQuickStartInput(e.target.value)} placeholder="Type a genre or vibe to filter..." className="w-full px-4 py-2.5 rounded-xl text-sm" style={{ border: '1px solid ' + EARTH.stone[300], backgroundColor: '#fff' }} />
              </div>
              {aiSuggestions.length > 0 && (<div className="mb-4 flex flex-wrap gap-2">{aiSuggestions.map((s, i) => (<button key={s.id + '-' + i} onClick={() => handleSelectTemplate(s)} className="px-3 py-1.5 rounded-lg text-sm font-medium" style={{ backgroundColor: EARTH.sage[100], color: EARTH.sage[700], border: '1px solid ' + EARTH.sage[300] }}>{s.genre}: {s.name}</button>))}</div>)}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(GENRE_TEMPLATES).map(([genre, templates]) => (
                  <div key={genre} className="relative">
                    <button onClick={() => setOpenGenreDropdown(openGenreDropdown === genre ? null : genre)} className="w-full p-3 rounded-xl text-left" style={{ backgroundColor: '#fff', border: '1px solid ' + (openGenreDropdown === genre ? EARTH.sage[400] : EARTH.stone[200]) }}><div className="font-semibold text-sm mb-0.5" style={{ color: EARTH.stone[800] }}>{genre}</div><div className="text-xs" style={{ color: EARTH.stone[500] }}>{templates.length} templates</div></button>
                    {openGenreDropdown === genre && (<div className="absolute top-full left-0 right-0 mt-1 rounded-xl shadow-xl z-20 max-h-64 overflow-y-auto" style={{ backgroundColor: '#fff', border: '1px solid ' + EARTH.stone[200] }}>{templates.map((t, i) => (<button key={t.id + '-' + i} onClick={() => handleSelectTemplate(t)} className="w-full p-3 text-left hover:bg-stone-50" style={{ borderBottom: i < templates.length - 1 ? '1px solid ' + EARTH.stone[100] : 'none' }}><div className="font-medium text-sm" style={{ color: EARTH.stone[700] }}>{t.name}</div><div className="text-xs flex items-center gap-2" style={{ color: EARTH.stone[500] }}>{t.sections.length} sections <span className="px-1.5 py-0.5 rounded text-xs" style={{ backgroundColor: t.energy === 'dynamic' ? EARTH.terracotta[100] : EARTH.sage[100], color: t.energy === 'dynamic' ? EARTH.terracotta[700] : EARTH.sage[700] }}>{t.energy}</span></div></button>))}</div>)}
                  </div>
                ))}
              </div>
              <div className="text-center mt-4"><button onClick={handleStartBlank} className="px-6 py-2 rounded-xl text-sm font-medium" style={{ backgroundColor: EARTH.stone[800], color: EARTH.sand[100] }}>Start with blank canvas</button></div>
            </>
          )}
        </div>
      </div>
      {showNamingDialog && (<div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}><div className="w-full max-w-md p-6 rounded-2xl shadow-xl" style={{ backgroundColor: '#fff' }}><h2 className="text-xl font-semibold mb-4" style={{ color: EARTH.stone[800] }}>Name your project</h2><input type="text" value={tempProjectName} onChange={(e) => setTempProjectName(e.target.value)} className="w-full px-4 py-3 rounded-xl mb-4" style={{ border: '1px solid ' + EARTH.stone[300] }} placeholder="Project name" /><div className="flex justify-end gap-3"><button onClick={() => setShowNamingDialog(false)} className="px-6 py-2 rounded-lg" style={{ color: EARTH.stone[600] }}>Cancel</button><button onClick={handleConfirmProject} className="px-6 py-2 rounded-lg" style={{ backgroundColor: EARTH.sage[600], color: '#fff' }}>Create Project</button></div></div></div>)}
      <ReferenceTrackAnalyzer isOpen={showRefAnalyzer} onClose={() => setShowRefAnalyzer(false)} onImport={handleRefImport} />
      <SmartGeneratorPanel isOpen={showAIGenerator} onClose={() => setShowAIGenerator(false)} onGenerate={handleAIGenerate} currentSections={canvasObjects} />
      <LoopAnalyzerPanel isOpen={showLoopAnalyzer} onClose={() => setShowLoopAnalyzer(false)} onGenerate={handleAIGenerate} />
      <TrackAnalyzerDialog isOpen={showTrackAnalyzer} onClose={() => setShowTrackAnalyzer(false)} onGenerate={handleAIGenerate} />
    </div>
  );
  }

  return (
    <div className="h-screen flex flex-col" style={{ backgroundColor: CANVAS_THEMES[canvasTheme].bg, fontFamily: TYPOGRAPHY.body }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3.5 glass-panel" style={{ borderBottom: '2px solid ' + EARTH.stone[200], boxShadow: '0 1px 3px rgba(0,0,0,0.03)' }}>
        <div className="flex items-center gap-4">
          <button onClick={() => setShowSavePrompt(true)} className="p-2 rounded-lg hover:bg-stone-100 transition-colors" style={{ color: EARTH.stone[500] }}><Home className="w-5 h-5" /></button>
          <div className="flex items-center gap-3">
            <span className="text-lg anton-header" style={{ fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'none' }}><span style={{ color: EARTH.stone[700] }}>arrangement</span><span style={{ color: EARTH.terracotta[500] }}>Lab</span></span>
            <span className="text-sm font-medium" style={{ color: EARTH.stone[700] }}>{projectName || 'Untitled'}</span>
            <span className="text-xs px-2 py-1 rounded-md font-medium" style={{
              backgroundColor: saveStatus === 'saved' ? EARTH.sage[100] : saveStatus === 'saving' ? EARTH.sand[100] : EARTH.stone[100],
              color: saveStatus === 'saved' ? EARTH.sage[700] : saveStatus === 'saving' ? EARTH.sand[700] : EARTH.stone[500],
              border: '1px solid rgba(214, 211, 209, 0.3)'
            }}>
              {saveStatus === 'saved' ? 'Saved' : saveStatus === 'saving' ? 'Saving...' : 'Unsaved'}
            </span>
            <span className="text-xs px-2 py-1 rounded-md" style={{ backgroundColor: EARTH.stone[100], color: EARTH.stone[600], border: '1px solid rgba(214, 211, 209, 0.3)' }}>{totalBars} bars • {totalDuration}</span>
            <span className="text-xs hidden lg:inline" style={{ color: EARTH.stone[400] }} title="Each bar is ~4 beats of music">({canvasObjects.filter(o => o.type === 'section').length} parts)</span>
            {trialDaysLeft !== undefined && (
              <div className="flex items-center gap-1.5">
                <TrialBadge daysLeft={trialDaysLeft} />
                <button
                  onClick={() => setShowLicenseDialog(true)}
                  className="text-xs font-medium hover:underline"
                  style={{ color: EARTH.terracotta[500], background: 'none', border: 'none', cursor: 'pointer', textUnderlineOffset: '2px' }}
                >
                  Activate
                </button>
              </div>
            )}
            {trialDaysLeft === undefined && isLicensed() && (
              <button
                onClick={() => setShowManageLicense(true)}
                className="text-xs font-medium hover:underline"
                style={{ color: EARTH.stone[400], background: 'none', border: 'none', cursor: 'pointer', textUnderlineOffset: '2px' }}
                title="Manage your license"
              >
                License
              </button>
            )}
            <div className="w-px h-5 mx-1" style={{ backgroundColor: EARTH.stone[200] }} />
            <div className="flex items-center gap-2 px-2 py-1 rounded-lg" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
              <div className="flex items-center gap-1">
                <span className="text-xs" style={{ color: EARTH.stone[500] }} title="Beats Per Minute — how fast your track is">BPM:</span>
                <input
                  type="number"
                  value={projectTempo}
                  onChange={(e) => { const t = parseInt(e.target.value) || 120; setProjectTempo(t); setCanvasObjects(p => applyTempoToSections(p, t)); logChange('Changed master BPM to ' + e.target.value); }}
                  className="w-12 text-xs px-1 py-0.5 rounded border text-center font-medium"
                  style={{ borderColor: EARTH.stone[300], color: EARTH.stone[700] }}
                  min="20"
                  max="300"
                />
              </div>
              <div className="w-px h-4" style={{ backgroundColor: EARTH.stone[200] }} />
              <div className="flex items-center gap-1">
                <span className="text-xs" style={{ color: EARTH.stone[500] }}>Key:</span>
                <select
                  value={projectKey}
                  onChange={(e) => { const k = e.target.value; setProjectKey(k); setCanvasObjects(p => applyKeyToSections(p, k)); logChange('Changed master key to ' + k); }}
                  className="text-xs px-1 py-0.5 rounded border font-medium al-select"
                  style={{ borderColor: EARTH.stone[300], color: EARTH.stone[700] }}
                >
                  {KEY_SIGNATURES.map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Export buttons */}
          <button onClick={exportProject} className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: EARTH.stone[100], color: EARTH.stone[700] }} title="Save project file">
            <Folder className="w-4 h-4" />Save As
          </button>
          {/* MIDI export removed — DAW integration TBD */}
          <div className="w-px h-6 mx-2" style={{ backgroundColor: EARTH.stone[200] }} />
          <button onClick={() => setShowAIGenerator(true)} className="p-2.5 rounded-lg transition-colors" style={{ backgroundColor: EARTH.terracotta[100], color: EARTH.terracotta[600] }} title="Smart Generator"><Brain className="w-5 h-5" /></button>
          <button onClick={() => setShowLoopAnalyzer(true)} className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all neo-shadow hover:shadow-md" style={{ backgroundColor: EARTH.terracotta[500], color: '#fff', borderRadius: '8px', fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase' }} title="Loop → Track"><Upload className="w-4 h-4" />LOOP → TRACK</button>
          <button onClick={() => setShowTrackAnalyzer(true)} className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all neo-shadow" style={{ background: `linear-gradient(135deg, ${EARTH.olive[600]}, ${EARTH.forest[600]})`, color: '#fff', borderRadius: '10px', fontFamily: TYPOGRAPHY.display, letterSpacing: '0.8px', textTransform: 'uppercase', boxShadow: `0 0 12px ${EARTH.olive[600]}33, 0 2px 8px rgba(0,0,0,0.15)`, border: `2px solid ${EARTH.olive[400]}` }} title="Analyze any published track's structure"><Search className="w-5 h-5" />ANALYZE TRACK</button>
          <button onClick={() => setShowLyricist(!showLyricist)} className="p-2.5 rounded-lg transition-colors" style={{ backgroundColor: showLyricist ? EARTH.olive[100] : 'transparent', color: showLyricist ? EARTH.olive[700] : EARTH.stone[500] }} title="Songwriting Pad"><BookOpen className="w-5 h-5" /></button>
          <button onClick={() => setShowAISuggestions(!showAISuggestions)} className="p-2.5 rounded-lg transition-colors" style={{ backgroundColor: showAISuggestions ? EARTH.sage[100] : 'transparent', color: showAISuggestions ? EARTH.sage[600] : EARTH.stone[500] }} title="Smart Suggestions"><Wand2 className="w-5 h-5" /></button>
          <div className="w-px h-6 mx-1" style={{ backgroundColor: EARTH.stone[200] }} />
          <button onClick={() => { setShowChangeLog(!showChangeLog); if (!showChangeLog) { setShowComments(false); setShowAccountability(false); setShowCoaching(false); } }} className="p-2.5 rounded-lg transition-colors" style={{ backgroundColor: showChangeLog ? EARTH.sage[100] : 'transparent', color: showChangeLog ? EARTH.sage[600] : EARTH.stone[500] }} title="Change Log"><Clock className="w-5 h-5" /></button>
          <button onClick={() => { setShowComments(!showComments); if (!showComments) { setShowChangeLog(false); setShowAccountability(false); setShowCoaching(false); } }} className="p-2.5 rounded-lg relative transition-colors" style={{ backgroundColor: showComments ? EARTH.sand[100] : 'transparent', color: showComments ? EARTH.sand[600] : EARTH.stone[500] }} title="Comments">
            <MessageSquare className="w-5 h-5" />
            {comments.length > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full text-xs flex items-center justify-center font-medium" style={{ backgroundColor: EARTH.terracotta[500], color: '#fff', fontSize: '10px' }}>{comments.length}</span>}
          </button>
          <button onClick={() => { setShowAccountability(!showAccountability); if (!showAccountability) { setShowChangeLog(false); setShowComments(false); setShowCoaching(false); } }} className="p-2.5 rounded-lg transition-colors" style={{ backgroundColor: showAccountability ? EARTH.terracotta[100] : 'transparent', color: showAccountability ? EARTH.terracotta[600] : EARTH.stone[500] }} title="Focus"><Target className="w-5 h-5" /></button>
          <button onClick={() => { setShowCoaching(!showCoaching); if (!showCoaching) { setShowChangeLog(false); setShowComments(false); setShowAccountability(false); } }} className="p-2.5 rounded-lg transition-colors" style={{ backgroundColor: showCoaching ? EARTH.forest[100] : 'transparent', color: showCoaching ? EARTH.forest[600] : EARTH.stone[500] }} title="Production Coach (Shift+C)"><BookOpen className="w-5 h-5" /></button>
          <a href="/help" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-lg transition-colors flex items-center justify-center" style={{ color: EARTH.stone[400], textDecoration: 'none' }} title="Help & docs"><HelpCircle className="w-5 h-5" /></a>
        </div>
      </div>
      {/* CollaborationPanel removed */}

      {/* Save before leaving prompt */}
      {showSavePrompt && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(26,26,26,0.6)', backdropFilter: 'blur(8px)',
        }}>
          <div style={{
            backgroundColor: EARTH.cream, borderRadius: '16px', padding: '40px',
            maxWidth: '420px', width: '90%', textAlign: 'center',
            boxShadow: '0 25px 50px rgba(0,0,0,0.25)',
            border: `1px solid ${EARTH.stone[200]}`,
            fontFamily: TYPOGRAPHY.body,
          }}>
            <h2 style={{ fontFamily: TYPOGRAPHY.display, fontSize: '22px', color: EARTH.ink, marginBottom: '10px', textTransform: 'lowercase' }}>
              save before leaving?
            </h2>
            <p style={{ color: EARTH.stone[500], fontSize: '14px', lineHeight: '1.6', marginBottom: '28px' }}>
              Your project <strong style={{ color: EARTH.stone[700] }}>{projectName || 'Untitled'}</strong> has unsaved changes. Save a copy before going home?
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={() => { exportProject(); resetMockup(); setShowSavePrompt(false); setCurrentView('welcome'); }}
                style={{
                  padding: '12px 24px', borderRadius: '10px', fontSize: '15px', fontWeight: 700,
                  backgroundColor: EARTH.terracotta[500], color: '#fff', border: 'none', cursor: 'pointer',
                  fontFamily: TYPOGRAPHY.body,
                }}
              >
                Save & go home
              </button>
              <button
                onClick={() => { resetMockup(); setShowSavePrompt(false); setCurrentView('welcome'); }}
                style={{
                  padding: '12px 24px', borderRadius: '10px', fontSize: '15px', fontWeight: 600,
                  backgroundColor: EARTH.stone[100], color: EARTH.stone[700], border: 'none', cursor: 'pointer',
                  fontFamily: TYPOGRAPHY.body,
                }}
              >
                Discard & go home
              </button>
              <button
                onClick={() => setShowSavePrompt(false)}
                style={{
                  padding: '10px', fontSize: '14px', fontWeight: 500,
                  background: 'none', color: EARTH.stone[400], border: 'none', cursor: 'pointer',
                  fontFamily: TYPOGRAPHY.body,
                }}
              >
                Cancel — keep working
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center justify-between px-5 py-2.5 glass-panel relative z-20" style={{ borderBottom: '1px solid ' + EARTH.stone[200] }}>
        <div className="flex items-center gap-1">
          {[{ id: 'select', icon: MousePointer, shortcut: 'V', label: 'Select' }, { id: 'hand', icon: Hand, shortcut: 'H', label: 'Pan' }, { id: 'connect', icon: GitBranch, shortcut: 'C', label: 'Connect' }].map(t => (<button key={t.id} onClick={() => setTool(t.id)} className="px-3 py-2.5 rounded-lg flex items-center gap-1.5 transition-colors" style={{ backgroundColor: tool === t.id ? EARTH.terracotta[100] : 'transparent', color: tool === t.id ? EARTH.terracotta[600] : EARTH.stone[500] }} title={`${t.label} (${t.shortcut})`}><t.icon className="w-4 h-4" /><span className="text-xs font-medium hidden sm:inline">{t.shortcut}</span></button>))}
          <div className="w-px h-6 mx-2" style={{ backgroundColor: EARTH.stone[200] }} />

          {/* Arrow color picker when connect tool is active */}
          {tool === 'connect' && (
            <div className="flex items-center gap-1 mr-2">
              <span className="text-xs mr-1" style={{ color: EARTH.stone[500] }}>Color:</span>
              {CONNECTOR_COLORS.map(c => (
                <button
                  key={c.id}
                  onClick={() => setSelectedArrowColor(c.id)}
                  className="w-5 h-5 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    backgroundColor: c.color,
                    borderColor: selectedArrowColor === c.id ? EARTH.stone[800] : 'transparent'
                  }}
                />
              ))}
              <div className="w-px h-6 mx-1" style={{ backgroundColor: EARTH.stone[200] }} />
            </div>
          )}

          <div className="relative">
            <button onClick={() => setShowSectionPicker(!showSectionPicker)} className="p-2 rounded-lg" style={{ color: EARTH.stone[500], backgroundColor: showSectionPicker ? EARTH.sage[100] : 'transparent' }} title="Add Section"><Plus className="w-5 h-5" /></button>
            {showSectionPicker && (
              <div className="absolute top-full left-0 mt-1 bg-white rounded-xl shadow-elevated border z-50 py-2 min-w-[180px]" style={{ borderColor: EARTH.stone[200], boxShadow: '0 4px 24px rgba(41, 37, 36, 0.08), 0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="px-3 py-1 text-xs font-medium" style={{ color: EARTH.stone[400] }}>Add Section</div>
                {SECTION_TYPES.map(st => (
                  <button
                    key={st.id}
                    onClick={() => {
                      const id = Date.now();
                      setCanvasObjects(p => [...p, {
                        id,
                        type: 'section',
                        name: st.name,
                        bars: st.defaultBars,
                        key: projectKey,
                        tempo: projectTempo,
                        instruments: '',
                        status: 'not-started',
                        sectionType: st.id,
                        customColor: null,
                        x: 100 - pan.x / zoom,
                        y: 100 - pan.y / zoom
                      }]);
                      logChange('Added ' + st.name + ' section');
                      setShowSectionPicker(false);
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-stone-50 flex items-center gap-2"
                    style={{ color: EARTH.stone[700] }}
                  >
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: SECTION_COLORS[st.id]?.accent || EARTH.stone[400] }} />
                    {st.name}
                    <span className="ml-auto text-xs" style={{ color: EARTH.stone[400] }}>{st.defaultBars}b</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button onClick={() => { const id = Date.now(); setCanvasObjects(p => [...p, { id, type: 'sticky', text: 'Note', color: STICKY_COLORS[0], x: 150 - pan.x / zoom, y: 150 - pan.y / zoom }]); logChange('Added note'); }} className="p-2 rounded-lg" style={{ color: EARTH.stone[500] }} title="Add Note"><StickyNote className="w-5 h-5" /></button>
          <div className="w-px h-6 mx-1" style={{ backgroundColor: EARTH.stone[200] }} />
          <button onClick={() => setSnapToGrid(!snapToGrid)} className="p-2 rounded-lg" style={{ backgroundColor: snapToGrid ? EARTH.sage[100] : 'transparent', color: snapToGrid ? EARTH.sage[700] : EARTH.stone[500] }} title="Snap to Grid"><Grid3x3 className="w-5 h-5" /></button>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(p => Math.max(0.1, p * (1 - ZOOM_FACTOR * 3)))} className="p-1 rounded" style={{ color: EARTH.stone[500] }}><ZoomOut className="w-4 h-4" /></button>
          <span className="text-sm w-12 text-center" style={{ color: EARTH.stone[600] }}>{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(p => Math.min(3, p * (1 + ZOOM_FACTOR * 3)))} className="p-1 rounded" style={{ color: EARTH.stone[500] }}><ZoomIn className="w-4 h-4" /></button>
          <button onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} className="p-1 rounded ml-2" style={{ color: EARTH.stone[500] }} title="Reset View"><Maximize2 className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Auto-backup nudge — projects only live in this browser's localStorage */}
      {showBackupNudge && (
        <div className="px-5 pt-3">
          <BackupNudge
            onExport={exportProject}
            onDismiss={() => { snoozeBackupNudge(); setShowBackupNudge(false); }}
          />
        </div>
      )}

      {/* Post-Build Coaching Banner */}
      {showCoachingBanner && canvasObjects.filter(o => o.type === 'section').length > 0 && canvasObjects.filter(o => o.type === 'section').every(o => o.status === 'not-started' || !o.status) && (
        <div className="px-5 py-3 fade-up flex items-center justify-between" style={{ backgroundColor: EARTH.olive[50], borderBottom: `1px solid ${EARTH.olive[200]}` }}>
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: EARTH.olive[200] }}>
              <Sparkles className="w-4 h-4" style={{ color: EARTH.olive[700] }} />
            </div>
            <div>
              <span className="text-sm font-semibold" style={{ color: EARTH.olive[800] }}>Your song blueprint is ready! </span>
              <span className="text-sm" style={{ color: EARTH.olive[600] }}>Click any card to see production tips. Work left to right — start with the first section in your DAW. Mark sections "In Progress" as you go.</span>
            </div>
          </div>
          <button onClick={() => setShowCoachingBanner(false)} className="p-1 rounded-full flex-shrink-0 hover:bg-white/50" style={{ color: EARTH.olive[500] }}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Main Canvas Area */}
      <div className="flex-1 flex overflow-hidden">
        <div ref={canvasRef} className="flex-1 relative overflow-hidden" style={{ backgroundColor: CANVAS_THEMES[canvasTheme].bg, cursor: tool === 'hand' ? 'grab' : 'default' }} onMouseDown={handleCanvasMouseDown} onMouseMove={handleCanvasMouseMove} onMouseUp={handleCanvasMouseUp} onMouseLeave={handleCanvasMouseUp} onDoubleClick={handleCanvasDoubleClick}>
          <svg width="100%" height="100%" style={{ overflow: 'visible' }}>
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill={CANVAS_THEMES[canvasTheme].grid} /></pattern>
              {/* Colored arrowheads */}
              {CONNECTOR_COLORS.map(c => (
                <marker key={c.id} id={`arrowhead-${c.id}`} markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill={c.color} />
                </marker>
              ))}
              <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto"><polygon points="0 0, 10 3.5, 0 7" fill={EARTH.stone[500]} /></marker>
            </defs>
            <g style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}>
              <rect x="-50000" y="-50000" width="100000" height="100000" fill="url(#grid)" style={{ pointerEvents: 'none' }} />
              {/* Arrows rendered BEFORE cards so they always appear behind card bodies */}
              {renderArrows()}
              {canvasObjects.map(obj => {
                const isSelected = selectedObjects.includes(obj.id);
                const isHovered = hoveredObject === obj.id;
                const showHandles = isSelected || isHovered || tool === 'connect';

                if (obj.type === 'section') {
                  // Energy-coded color system: hot cards for high energy, cool for low
                  const hasEnergyData = obj.energy != null;
                  const energyLevel = obj.energy || 0;
                  let colorScheme;
                  if (obj.customColor) {
                    colorScheme = CARD_COLORS.find(c => c.id === obj.customColor);
                  } else if (hasEnergyData) {
                    // Energy-driven color coding — translucent vibrant cards
                    if (energyLevel >= 0.85) {
                      // Scorching: warm terracotta glow
                      colorScheme = { bg: 'rgba(194, 65, 40, 0.18)', border: EARTH.terracotta[500], text: EARTH.stone[800], accent: EARTH.terracotta[500] };
                    } else if (energyLevel >= 0.7) {
                      // Hot: soft terracotta
                      colorScheme = { bg: 'rgba(194, 65, 40, 0.12)', border: EARTH.terracotta[400], text: EARTH.stone[800], accent: EARTH.terracotta[400] };
                    } else if (energyLevel >= 0.5) {
                      // Medium: warm olive
                      colorScheme = { bg: 'rgba(140, 130, 50, 0.12)', border: EARTH.olive[500], text: EARTH.stone[800], accent: EARTH.olive[600] };
                    } else if (energyLevel >= 0.3) {
                      // Cool: soft forest
                      colorScheme = { bg: 'rgba(50, 120, 70, 0.12)', border: EARTH.forest[400], text: EARTH.stone[800], accent: EARTH.forest[600] };
                    } else {
                      // Cold: soft blue-stone
                      colorScheme = { bg: 'rgba(80, 90, 110, 0.10)', border: EARTH.stone[400], text: EARTH.stone[800], accent: EARTH.stone[500] };
                    }
                  } else {
                    colorScheme = SECTION_COLORS[obj.sectionType] || SECTION_COLORS.default;
                  }
                  const statusColor = STATUS_COLORS[obj.status];
                  const sectionDuration = calculateDuration(obj.bars, obj.tempo);
                  // Energy bar color
                  const energyBarColor = energyLevel >= 0.8 ? '#ef4444' : energyLevel >= 0.6 ? '#f97316' : energyLevel >= 0.4 ? '#eab308' : energyLevel >= 0.2 ? '#22c55e' : '#3b82f6';
                  // Element tags from loop analyzer
                  const elementTags = obj.elements || [];
                  const guidanceTips = obj.productionGuidance || obj.sectionGuidance || null;
                  return (
                    <g key={obj.id}
                      transform={`translate(${obj.x}, ${obj.y})`} style={{ filter: "drop-shadow(0 2px 8px rgba(41, 37, 36, 0.06))" }}
                      onMouseEnter={() => setHoveredObject(obj.id)}
                      onMouseLeave={() => setHoveredObject(null)}
                    >
                      {/* Glow effect for high-energy cards */}
                      {hasEnergyData && energyLevel >= 0.8 && (
                        <rect x={-3} y={-3} width={206} height={246} rx={18} fill="none" stroke={EARTH.terracotta[500]} strokeWidth={1} opacity={0.25} />
                      )}
                      {/* White base for translucent cards */}
                      <rect x={0} y={0} width={200} height={240} rx={14} fill="#ffffff" style={{ pointerEvents: 'none' }} />
                      <rect x={0} y={0} width={200} height={240} rx={14} fill={colorScheme.bg} stroke={isSelected ? EARTH.terracotta[500] : isHovered ? EARTH.terracotta[300] : colorScheme.border} strokeWidth={isSelected ? 2.5 : isHovered ? 1.5 : 1} style={{ cursor: tool === 'connect' ? 'crosshair' : 'move' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          cardClickedRef.current = true;
                          const rect = canvasRef.current.getBoundingClientRect();
                          const pos = screenToCanvas(e.clientX - rect.left, e.clientY - rect.top);

                          // Check if this is a multi-select drag BEFORE changing selection
                          const isPartOfMultiSelect = selectedObjects.length > 1 && selectedObjects.includes(obj.id);

                          if (e.shiftKey) {
                            setSelectedObjects(p => p.includes(obj.id) ? p.filter(i => i !== obj.id) : [...p, obj.id]);
                          } else if (!isPartOfMultiSelect) {
                            if (!selectedObjects.includes(obj.id)) setSelectedObjects([obj.id]);
                          }

                          if (isPartOfMultiSelect) {
                            const offsets = {};
                            canvasObjects.filter(o => selectedObjects.includes(o.id)).forEach(o => {
                              offsets[o.id] = { x: o.x - pos.x, y: o.y - pos.y };
                            });
                            multiDragOffsetsRef.current = offsets;
                            setMultiDragOffsets(offsets);
                          }

                          setDraggedObject(obj.id);
                          setDragOffset({ x: pos.x - obj.x, y: pos.y - obj.y });
                          setHasDragged(false);
                          setSelectedArrow(null);
                          setArrowToolbarPos(null);
                        }}
                        onDoubleClick={() => setEditingObject(obj.id)}
                      />

                      {/* Energy accent stripe on left edge */}
                      {hasEnergyData && (
                        <rect x={0} y={16} width={4} height={Math.round(energyLevel * 208)} rx={2} fill={energyBarColor} opacity={0.9} style={{ pointerEvents: 'none' }} />
                      )}

                      {/* YOUR LOOP badge - prominent at top */}
                      {obj.isLoopSection && (
                        <g style={{ pointerEvents: 'none' }}>
                          <rect x={110} y={4} width={84} height={18} rx={9} fill={EARTH.terracotta[500]} />
                          <text x={152} y={16} fontSize="9" fill="#fff" textAnchor="middle" fontWeight="700" style={{ letterSpacing: '0.5px' }}>♫ YOUR LOOP</text>
                        </g>
                      )}

                      {/* Card content — pure SVG, no foreignObject (cross-browser reliable) */}
                      <defs>
                        <clipPath id={`card-clip-${obj.id}`}>
                          <rect x={6} y={22} width={188} height={180} />
                        </clipPath>
                      </defs>
                      <g clipPath={`url(#card-clip-${obj.id})`} style={{ pointerEvents: 'none' }}>
                        {/* Title */}
                        <circle cx={16} cy={34} r={4} fill={hasEnergyData ? energyBarColor : colorScheme.accent} />
                        <text x={26} y={38} fontSize="12" fontWeight="700" fill={colorScheme.text} style={{ pointerEvents: 'none', letterSpacing: '0.3px' }}>{obj.name.length > 14 ? obj.name.slice(0, 14) + '…' : obj.name}</text>
                        {hasEnergyData && (
                          <text x={190} y={38} fontSize="9" fontWeight="600" fill={energyBarColor} textAnchor="end" style={{ pointerEvents: 'none' }}>{Math.round(energyLevel * 100)}%</text>
                        )}
                        {/* Energy bar */}
                        {hasEnergyData && (
                          <>
                            <rect x={10} y={44} width={180} height={3} rx={2} fill={colorScheme.border} opacity={0.3} />
                            <rect x={10} y={44} width={Math.round(energyLevel * 180)} height={3} rx={2} fill={energyBarColor} />
                          </>
                        )}
                        {/* Data row */}
                        <text x={10} y={62} fontSize="9" fill={colorScheme.text} style={{ pointerEvents: 'none' }}>
                          <tspan opacity={0.5}>Bars: </tspan><tspan fontWeight="500">{obj.bars}</tspan>
                          <tspan opacity={0.5} dx={8}>Key: </tspan><tspan fontWeight="500">{obj.key}</tspan>
                          <tspan opacity={0.5} dx={8}>BPM: </tspan><tspan fontWeight="500">{obj.tempo}</tspan>
                        </text>
                        <text x={10} y={78} fontSize="9" fill={colorScheme.text} opacity={0.5} style={{ pointerEvents: 'none' }}>{sectionDuration}</text>
                        {/* Element tags */}
                        {elementTags.length > 0 && elementTags.slice(0, 4).map((el, idx) => (
                          <g key={idx}>
                            <rect x={10 + idx * 44} y={86} width={40} height={14} rx={3} fill={energyLevel >= 0.7 ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)'} />
                            <text x={30 + idx * 44} y={96} fontSize="7" fontWeight="600" fill={colorScheme.text} opacity={0.8} textAnchor="middle" style={{ pointerEvents: 'none', textTransform: 'uppercase' }}>{el.length > 6 ? el.slice(0, 6) : el}</text>
                          </g>
                        ))}
                        {/* Production guidance — wrap manually */}
                        {guidanceTips && (() => {
                          const rawTip = typeof guidanceTips === 'string' ? guidanceTips :
                            guidanceTips.mixTips ? guidanceTips.mixTips[0] :
                            guidanceTips.tips ? guidanceTips.tips[0] : '';
                          // Strip internal "[…principle]" annotations so a collapsed card never
                          // shows a half-open bracket ("[Arrangement contras…"); they belong to
                          // the expanded view, not the one-glance card.
                          const tipText = (rawTip || '').replace(/\s*\[[^\]]*\]/g, '').trim();
                          if (!tipText) return null;
                          const maxChars = 32;
                          const lines = [];
                          let remaining = tipText;
                          for (let i = 0; i < 3 && remaining.length > 0; i++) {
                            if (remaining.length <= maxChars) { lines.push(remaining); break; }
                            let cut = remaining.lastIndexOf(' ', maxChars);
                            if (cut <= 0) cut = maxChars;
                            lines.push(remaining.slice(0, cut));
                            remaining = remaining.slice(cut).trimStart();
                          }
                          if (remaining.length > 0 && lines.length === 3) {
                            lines[2] = lines[2].slice(0, -1) + '…';
                          }
                          return (
                            <>
                              <text x={10} y={112} fontSize="8.5" fontWeight="600" fill={colorScheme.accent || colorScheme.text} opacity={0.6} style={{ pointerEvents: 'none', textTransform: 'uppercase', letterSpacing: '0.5px' }}>PRODUCER TIP</text>
                              {lines.map((line, li) => (
                                <text key={li} x={10} y={124 + li * 12} fontSize="9" fill={colorScheme.text} opacity={0.45} fontStyle="italic" style={{ pointerEvents: 'none' }}>{line}</text>
                              ))}
                            </>
                          );
                        })()}
                      </g>

                      {/* Status dropdown */}
                      <foreignObject x={8} y={206} width={184} height={28} style={{ pointerEvents: 'auto' }}>
                        <select xmlns="http://www.w3.org/1999/xhtml" value={obj.status} onMouseDown={(e) => e.stopPropagation()} onChange={(e) => { updateObject(obj.id, { status: e.target.value }); logChange('Updated ' + obj.name + ' status'); }} style={{ width: '100%', padding: '3px 8px', borderRadius: '6px', letterSpacing: '0.3px', fontSize: '11px', fontWeight: '500', backgroundColor: statusColor.bg, color: statusColor.text, border: '1px solid ' + statusColor.border }}><option value="not-started">Not Started</option><option value="in-progress">In Progress</option><option value="done">Done</option></select>
                      </foreignObject>

                      {/* Edit mode */}
                      {editingObject === obj.id && (
                        <foreignObject x={8} y={8} width={184} height={224} style={{ pointerEvents: 'auto' }} onMouseDown={(e) => e.stopPropagation()}>
                          <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '184px', padding: '8px', boxSizing: 'border-box', background: colorScheme.bg, borderRadius: '12px' }}>
                            <input type="text" value={obj.name} onChange={(e) => updateObject(obj.id, { name: e.target.value, sectionType: getSectionType(e.target.value) })} style={{ width: '100%', fontWeight: '600', fontSize: '14px', background: 'transparent', borderBottom: '1px solid ' + colorScheme.border, color: colorScheme.text, marginBottom: '8px' }} autoFocus />
                            <div style={{ fontSize: '12px', color: colorScheme.text, marginBottom: '4px' }}>Bars: <input type="number" value={obj.bars} onChange={(e) => updateObject(obj.id, { bars: parseInt(e.target.value) || 8 })} style={{ width: '50px', background: 'transparent', borderBottom: '1px solid ' + colorScheme.border, color: colorScheme.text, fontSize: '12px' }} /></div>
                            <div style={{ fontSize: '12px', color: colorScheme.text, marginBottom: '4px' }}>Key: <select value={obj.key} onChange={(e) => updateObject(obj.id, { key: e.target.value })} style={{ background: 'transparent', fontSize: '12px', color: colorScheme.text }}>{KEY_SIGNATURES.map(k => <option key={k} value={k}>{k}</option>)}</select></div>
                            <div style={{ fontSize: '12px', color: colorScheme.text, marginBottom: '4px' }}>BPM: <input type="number" value={obj.tempo} onChange={(e) => updateObject(obj.id, { tempo: parseInt(e.target.value) || 120 })} style={{ width: '50px', background: 'transparent', borderBottom: '1px solid ' + colorScheme.border, color: colorScheme.text, fontSize: '12px' }} /></div>
                            <div style={{ fontSize: '12px', color: colorScheme.text, marginBottom: '6px' }}>Inst: <input type="text" value={obj.instruments || ''} onChange={(e) => updateObject(obj.id, { instruments: e.target.value })} style={{ width: '100px', background: 'transparent', borderBottom: '1px solid ' + colorScheme.border, color: colorScheme.text, fontSize: '12px' }} placeholder="drums, bass" /></div>
                            <div>{CARD_COLORS.map(c => (<button key={c.id} onClick={() => updateObject(obj.id, { customColor: c.id })} style={{ width: '16px', height: '16px', borderRadius: '50%', border: '2px solid ' + (obj.customColor === c.id ? EARTH.stone[800] : c.border), backgroundColor: c.bg, marginRight: '4px' }} />))}</div>
                          </div>
                        </foreignObject>
                      )}
                      {showHandles && (
                        <>
                          <ConnectionHandle objId={obj.id} side="top" x={100} y={0} isHovered={isHovered} />
                          <ConnectionHandle objId={obj.id} side="right" x={200} y={120} isHovered={isHovered} />
                          <ConnectionHandle objId={obj.id} side="bottom" x={100} y={240} isHovered={isHovered} />
                          <ConnectionHandle objId={obj.id} side="left" x={0} y={120} isHovered={isHovered} />
                        </>
                      )}
                    </g>
                  );
                } else if (obj.type === 'sticky') {
                  const stickyColor = obj.color || STICKY_COLORS[0];
                  return (
                    <g key={obj.id}
                      transform={`translate(${obj.x}, ${obj.y})`} style={{ filter: "drop-shadow(0 2px 8px rgba(41, 37, 36, 0.06))" }}
                      onMouseEnter={() => setHoveredObject(obj.id)}
                      onMouseLeave={() => setHoveredObject(null)}
                    >
                      <rect x={0} y={0} width={180} height={140} rx={8} fill={stickyColor.color} stroke={isSelected ? EARTH.terracotta[500] : isHovered ? EARTH.terracotta[300] : stickyColor.border} strokeWidth={isSelected ? 3 : 1} style={{ cursor: 'move', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          const rect = canvasRef.current.getBoundingClientRect();
                          const pos = screenToCanvas(e.clientX - rect.left, e.clientY - rect.top);
                          setSelectedObjects([obj.id]);
                          setDraggedObject(obj.id);
                          setDragOffset({ x: pos.x - obj.x, y: pos.y - obj.y });
                          setSelectedArrow(null);
                          setArrowToolbarPos(null);
                        }}
                        onDoubleClick={() => setEditingObject(obj.id)}
                      />
                      {/* Non-edit mode: show text with pointer-events none so clicks pass through */}
                      {editingObject !== obj.id && (
                        <foreignObject x={0} y={0} width={180} height={140} style={{ pointerEvents: 'none' }}>
                          <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '180px', height: '140px', padding: '12px', boxSizing: 'border-box' }}>
                            <p style={{ fontSize: '14px', color: EARTH.stone[700], margin: 0 }}>{obj.text || 'Double-click to edit'}</p>
                          </div>
                        </foreignObject>
                      )}
                      {/* Edit mode: click outside to close */}
                      {editingObject === obj.id && (
                        <foreignObject x={0} y={0} width={180} height={140} style={{ pointerEvents: 'auto' }} onMouseDown={(e) => e.stopPropagation()}>
                          <div xmlns="http://www.w3.org/1999/xhtml" style={{ width: '180px', height: '140px', padding: '12px', boxSizing: 'border-box' }}>
                            <textarea value={obj.text} onChange={(e) => updateObject(obj.id, { text: e.target.value })} style={{ width: '100%', height: '80px', background: 'transparent', border: 'none', resize: 'none', fontSize: '14px', color: EARTH.stone[700], outline: 'none' }} autoFocus />
                            <div style={{ marginTop: '8px' }}>{STICKY_COLORS.map(c => (<button key={c.id} onClick={() => updateObject(obj.id, { color: c })} style={{ width: '16px', height: '16px', borderRadius: '50%', border: '1px solid ' + (obj.color?.id === c.id ? EARTH.stone[600] : c.border), backgroundColor: c.color, marginRight: '4px' }} />))}</div>
                          </div>
                        </foreignObject>
                      )}
                      {showHandles && (
                        <>
                          <ConnectionHandle objId={obj.id} side="top" x={90} y={0} isHovered={isHovered} />
                          <ConnectionHandle objId={obj.id} side="right" x={180} y={70} isHovered={isHovered} />
                          <ConnectionHandle objId={obj.id} side="bottom" x={90} y={140} isHovered={isHovered} />
                          <ConnectionHandle objId={obj.id} side="left" x={0} y={70} isHovered={isHovered} />
                        </>
                      )}
                    </g>
                  );
                }
                return null;
              })}
              {/* Arrows rendered above in the layer order — now behind cards */}
              {draggingConnection && connectionEnd && (<line x1={getObjectCenter(draggingConnection.fromId, draggingConnection.side).x} y1={getObjectCenter(draggingConnection.fromId, draggingConnection.side).y} x2={connectionEnd.x} y2={connectionEnd.y} stroke={EARTH.terracotta[400]} strokeWidth="2" strokeDasharray="5,5" />)}
              {isBoxSelecting && boxSelectStart && boxSelectEnd && (<rect x={Math.min(boxSelectStart.x, boxSelectEnd.x)} y={Math.min(boxSelectStart.y, boxSelectEnd.y)} width={Math.abs(boxSelectEnd.x - boxSelectStart.x)} height={Math.abs(boxSelectEnd.y - boxSelectStart.y)} fill="rgba(116, 140, 95, 0.1)" stroke={EARTH.sage[400]} strokeWidth="1" strokeDasharray="4" />)}
            </g>
          </svg>
          {selectedObjects.length > 0 && (<div className="absolute bottom-4 left-4 px-4 py-2 rounded-lg shadow-lg" style={{ backgroundColor: 'rgba(255,255,255,0.95)' }}><span className="text-sm font-medium" style={{ color: EARTH.stone[700] }}>{selectedObjects.length} selected</span><span className="text-xs ml-2" style={{ color: EARTH.stone[500] }}>Delete to remove</span></div>)}

          {/* Empty Canvas Coaching */}
          {canvasObjects.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
              <div className="text-center max-w-md pointer-events-auto p-10 rounded-2xl" style={{ backgroundColor: 'rgba(255,255,255,0.95)', boxShadow: '0 12px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)' }}>
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: `linear-gradient(135deg, ${EARTH.terracotta[100]}, ${EARTH.olive[100]})` }}>
                  <Music className="w-8 h-8" style={{ color: EARTH.terracotta[500] }} />
                </div>
                <h3 className="text-lg tracking-wide font-bold mb-2" style={{ color: EARTH.stone[800], fontFamily: TYPOGRAPHY.display }}>Your canvas is ready</h3>
                <p className="text-sm mb-6 leading-relaxed" style={{ color: EARTH.stone[500] }}>This is where your song takes shape. Each card represents a section of your track — intro, verse, chorus, drop — whatever your song needs.</p>
                <div className="space-y-2">
                  <button onClick={() => setShowLoopAnalyzer(true)} className="w-full py-2.5 transition-all duration-200 hover:-translate-y-0.5 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: EARTH.terracotta[500], color: '#fff' }}>
                    <Upload className="w-4 h-4" /> Upload a beat or loop
                  </button>
                  <button onClick={() => setShowTrackAnalyzer(true)} className="w-full py-2.5 transition-all duration-200 hover:-translate-y-0.5 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" style={{ background: `linear-gradient(135deg, ${EARTH.olive[600]}, ${EARTH.forest[600]})`, color: '#fff' }}>
                    <Search className="w-4 h-4" /> Analyze a song I love
                  </button>
                  <button onClick={() => setShowAIGenerator(true)} className="w-full py-2.5 transition-all duration-200 hover:-translate-y-0.5 px-4 rounded-xl text-sm font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: EARTH.sage[600], color: '#fff' }}>
                    <Sparkles className="w-4 h-4" /> Generate a structure for me
                  </button>
                  <p className="text-xs pt-2" style={{ color: EARTH.stone[400] }}>Or use the <strong>+</strong> button in the toolbar to add sections manually</p>
                </div>
              </div>
            </div>
          )}

          {/* Connector Toolbar - shows when arrow is selected */}
          <ConnectorToolbar
            arrow={selectedArrow}
            position={arrowToolbarPos}
            onChangeColor={changeArrowColor}
            onDelete={deleteSelectedArrow}
            onClose={() => { setSelectedArrow(null); setArrowToolbarPos(null); }}
          />
        </div>
      </div>

      {/* ── Unified Right-Edge Panel Tab Strip ── */}
      {!showComments && !showAccountability && !showCoaching && !showLyricist && (() => {
        const panelTabs = [
          { id: 'coaching', label: 'COACH',  icon: BookOpen,      color: EARTH.forest[600],      action: () => { setShowCoaching(true); setShowChangeLog(false); setShowComments(false); setShowAccountability(false); setShowLyricist(false); } },
          { id: 'focus',    label: 'FOCUS',  icon: Target,         color: EARTH.terracotta[500],  action: () => { setShowAccountability(true); setShowCoaching(false); setShowChangeLog(false); setShowComments(false); setShowLyricist(false); } },
          { id: 'comments', label: 'NOTES',  icon: MessageSquare,  color: '#a0845c',              action: () => { setShowComments(true); setShowCoaching(false); setShowChangeLog(false); setShowAccountability(false); setShowLyricist(false); } },
          { id: 'lyrics',   label: 'LYRICS', icon: PenTool,        color: EARTH.olive[600],       action: () => { setShowLyricist(true); setShowCoaching(false); setShowChangeLog(false); setShowComments(false); setShowAccountability(false); } },
        ];
        return (
          <div style={{ position: 'fixed', right: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '8px', zIndex: 19 }}>
            {panelTabs.map(tab => (
              <button
                key={tab.id}
                onClick={tab.action}
                title={tab.label}
                style={{
                  width: '52px', height: '130px',
                  backgroundColor: tab.color,
                  borderRadius: '14px 0 0 14px',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: '12px',
                  border: 'none', cursor: 'pointer',
                  boxShadow: '-3px 2px 14px rgba(0,0,0,0.20)',
                  transition: 'opacity 0.15s, transform 0.15s',
                  opacity: 0.93,
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'translateX(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.93'; e.currentTarget.style.transform = 'translateX(0)'; }}
              >
                <tab.icon style={{ width: '18px', height: '18px', color: '#fff', flexShrink: 0 }} />
                <span style={{
                  color: '#fff', fontSize: '10px', fontWeight: '800',
                  letterSpacing: '1.6px', textTransform: 'uppercase',
                  writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                  lineHeight: 1,
                }}>{tab.label}</span>
              </button>
            ))}
          </div>
        );
      })()}

      {/* Collapsible Right Panels */}
      <ChangeLogPanel isOpen={showChangeLog} onToggle={() => setShowChangeLog(!showChangeLog)} changeLog={changeLog} />
      {!showChangeLog && !showAccountability && <CommentsPanel isOpen={showComments} onToggle={() => setShowComments(!showComments)} comments={comments} onAddComment={addComment} />}
      {!showChangeLog && !showComments && !showCoaching && <AccountabilityWidget isOpen={showAccountability} onToggle={() => setShowAccountability(!showAccountability)} projectName={projectName} sections={canvasObjects.filter(o => o.type === 'section')} accountability={accountability} onAccountabilityChange={setAccountability} onMarkSectionComplete={(id) => { updateObject(id, { status: 'done' }); logChange('Marked section complete from Focus panel'); }} />}
      {!showChangeLog && !showComments && !showAccountability && (() => {
        const sections = canvasObjects.filter(o => o.type === 'section');
        const selectedSection = selectedObjects.length === 1 ? sections.find(s => s.id === selectedObjects[0]) : null;
        return <CoachingSidebar
          isOpen={showCoaching}
          onToggle={() => setShowCoaching(!showCoaching)}
          section={selectedSection || null}
          allSections={sections}
          projectTempo={projectTempo}
          projectKey={projectKey}
          coachingState={coachingState}
          onCoachingStateChange={setCoachingState}
          onUpdateSectionStatus={(id, status) => { updateObject(id, { status }); logChange(`Marked section ${status} from Coach`); }}
          onNavigateToSection={(id) => { setSelectedObjects([id]); }}
        />;
      })()}

      <AISuggestionCallout lastSection={lastSection} onAddSection={handleAddSuggestedSection} isVisible={showAISuggestions && !loopAudioUrl} />
      <LyricistSketchpad sections={canvasObjects} isOpen={showLyricist} onToggle={() => setShowLyricist(!showLyricist)} lyrics={lyrics} onLyricsChange={setLyrics} />
      <SmartGeneratorPanel isOpen={showAIGenerator} onClose={() => setShowAIGenerator(false)} onGenerate={handleAIGenerate} currentSections={canvasObjects} />
      <LoopAnalyzerPanel isOpen={showLoopAnalyzer} onClose={() => setShowLoopAnalyzer(false)} onGenerate={handleAIGenerate} />
      <TrackAnalyzerDialog isOpen={showTrackAnalyzer} onClose={() => setShowTrackAnalyzer(false)} onGenerate={handleAIGenerate} />

      {/* Arrangement Mockup Player */}
      {loopAudioUrl && canvasObjects.filter(o => o.type === 'section').length > 0 && (mockupState === 'ready' || mockupState === 'loading' || mockupState === 'playing' || mockupState === 'paused') && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-40" style={{
          backgroundColor: 'rgba(26,26,26,0.95)', backdropFilter: 'blur(16px)',
          borderRadius: '16px', padding: mockupExpanded ? '20px 28px' : '12px 20px',
          display: 'flex', flexDirection: 'column', gap: mockupExpanded ? '12px' : '8px',
          border: `1px solid ${EARTH.terracotta[500]}40`, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          minWidth: mockupExpanded ? '700px' : '380px', maxWidth: mockupExpanded ? '900px' : '500px',
          transition: 'all 0.25s ease',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button onClick={handleMockupPreview} style={{
              width: mockupExpanded ? '52px' : '40px', height: mockupExpanded ? '52px' : '40px',
              borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              backgroundColor: mockupState === 'playing' ? EARTH.terracotta[500] : EARTH.sage[500],
              border: 'none', cursor: (mockupState === 'loading' || mockupState === 'ready') ? (mockupState === 'loading' ? 'wait' : 'pointer') : 'pointer', transition: 'all 0.2s', flexShrink: 0,
            }}>
              {mockupState === 'loading' ? (
                <svg className="animate-spin" style={{ width: '16px', height: '16px' }} viewBox="0 0 24 24"><circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="#fff" strokeWidth="4" fill="none" /><path style={{ opacity: 0.75 }} fill="#fff" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
              ) : mockupState === 'playing' ? (
                <Square className={mockupExpanded ? 'w-5 h-5' : 'w-3.5 h-3.5'} style={{ color: '#fff' }} />
              ) : (
                <Play className={mockupExpanded ? 'w-5 h-5' : 'w-4 h-4'} style={{ color: '#fff', marginLeft: '2px' }} />
              )}
            </button>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: mockupExpanded ? '13px' : '11px', fontWeight: '700', color: EARTH.terracotta[400], margin: 0, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Arrangement Preview
              </p>
              <p style={{ fontSize: mockupExpanded ? '13px' : '10px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>
                {mockupCurrentSection !== null
                  ? `${canvasObjects.filter(o => o.type === 'section')[mockupCurrentSection]?.name || ''}`
                  : 'Preview'}
                {' · '}{Math.floor(mockupElapsed / 60)}:{String(Math.floor(mockupElapsed % 60)).padStart(2, '0')}
                {' / '}{mockupEngineRef.current ? `${Math.floor(mockupEngineRef.current.totalDuration / 60)}:${String(Math.floor(mockupEngineRef.current.totalDuration % 60)).padStart(2, '0')}` : totalDuration}
              </p>
            </div>
            <button onClick={() => setMockupExpanded(!mockupExpanded)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }} title={mockupExpanded ? 'Collapse' : 'Expand'}>
              <Maximize2 className="w-4 h-4" style={{ color: mockupExpanded ? EARTH.terracotta[400] : 'rgba(255,255,255,0.4)' }} />
            </button>
            <button onClick={handleMockupStop} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
              <X className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.4)' }} />
            </button>
          </div>
          {/* Progress bar with section markers */}
          <div onClick={handleMockupSeek} style={{
            width: '100%', height: mockupExpanded ? '10px' : '6px', backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '3px', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'height 0.2s',
          }}>
            <div style={{
              width: `${mockupProgress * 100}%`, height: '100%',
              backgroundColor: EARTH.terracotta[500], borderRadius: '3px',
              transition: 'width 0.05s linear'
            }} />
          </div>
          {/* Section markers */}
          <div style={{ display: 'flex', gap: '2px', height: mockupExpanded ? '24px' : '14px', transition: 'height 0.2s' }}>
            {canvasObjects.filter(o => o.type === 'section').map((s, i, arr) => {
              const totalBars = arr.reduce((sum, x) => sum + (x.bars || 8), 0);
              const widthPct = ((s.bars || 8) / totalBars) * 100;
              const isCurrent = mockupCurrentSection === i;
              return (
                <div key={s.id} style={{
                  width: `${widthPct}%`, fontSize: mockupExpanded ? '10px' : '7px', fontWeight: isCurrent ? '700' : '500',
                  color: isCurrent ? EARTH.terracotta[400] : 'rgba(255,255,255,0.3)',
                  textAlign: 'center', lineHeight: mockupExpanded ? '24px' : '14px', overflow: 'hidden', whiteSpace: 'nowrap',
                  borderBottom: isCurrent ? `2px solid ${EARTH.terracotta[500]}` : '2px solid transparent',
                  transition: 'all 0.15s', borderRadius: '2px',
                  backgroundColor: isCurrent ? 'rgba(255,255,255,0.05)' : 'transparent',
                }}>
                  {s.name}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Preview Arrangement button (when loop is loaded but mockup is idle) */}
      {loopAudioUrl && canvasObjects.filter(o => o.type === 'section').length > 0 && mockupState === 'idle' && (
        <button onClick={handleMockupPreview} className="fixed z-40" style={{
          bottom: '72px', left: '50%', transform: 'translateX(-50%)',
          backgroundColor: EARTH.terracotta[500], color: '#fff', border: 'none',
          borderRadius: '12px', padding: '8px 20px', fontSize: '12px', fontWeight: '600',
          cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)', transition: 'all 0.2s',
          letterSpacing: '0.02em'
        }}
        onMouseEnter={e => e.currentTarget.style.transform = 'translateX(-50%) scale(1.05)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'translateX(-50%)'}
        >
          <Play className="w-4 h-4" />
          Preview Full Arrangement
        </button>
      )}

      {/* Floating loop audio player */}
      {loopAudioUrl && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40" style={{
          backgroundColor: 'rgba(26,26,26,0.92)', backdropFilter: 'blur(12px)',
          borderRadius: '16px', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '12px',
          border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          minWidth: '320px'
        }}>
          <button onClick={() => {
            if (loopAudioRef.current) {
              if (isPlayingLoop) { loopAudioRef.current.pause(); } else { loopAudioRef.current.play(); }
              setIsPlayingLoop(!isPlayingLoop);
            }
          }} style={{
            width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            backgroundColor: isPlayingLoop ? EARTH.terracotta[500] : EARTH.sage[500], border: 'none', cursor: 'pointer',
            transition: 'all 0.2s'
          }}>
            {isPlayingLoop ? <Square className="w-3.5 h-3.5" style={{ color: '#fff' }} /> : <Play className="w-4 h-4" style={{ color: '#fff', marginLeft: '2px' }} />}
          </button>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#fff', margin: 0 }}>♫ Your Loop</p>
            <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.5)', margin: 0 }}>Reference playback</p>
          </div>
          <audio ref={loopAudioRef} src={loopAudioUrl} loop
            onPlay={() => setIsPlayingLoop(true)}
            onPause={() => setIsPlayingLoop(false)}
            onEnded={() => setIsPlayingLoop(false)}
          />
          <button onClick={() => setLoopAudioUrl(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
            <X className="w-4 h-4" style={{ color: 'rgba(255,255,255,0.4)' }} />
          </button>
        </div>
      )}

      {/* License activation dialog — available during trial */}
      {showLicenseDialog && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(26, 26, 26, 0.5)', backdropFilter: 'blur(4px)',
        }} onClick={() => setShowLicenseDialog(false)}>
          <div style={{
            backgroundColor: EARTH.cream, borderRadius: '16px', padding: '32px',
            maxWidth: '440px', width: '90%',
            boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
            border: `1px solid ${EARTH.stone[200]}`,
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: TYPOGRAPHY.display, fontSize: '22px', color: EARTH.ink, marginBottom: '8px', textTransform: 'lowercase' }}>
              activate license
            </h3>
            <p style={{ color: EARTH.stone[500], fontSize: '13px', marginBottom: '20px', fontFamily: TYPOGRAPHY.body }}>
              Enter the license key from your purchase confirmation email.
            </p>
            <LicenseKeyInput onActivated={() => { setShowLicenseDialog(false); if (onLicenseActivated) onLicenseActivated(); }} />
            <button
              onClick={() => setShowLicenseDialog(false)}
              style={{
                display: 'block', margin: '16px auto 0', background: 'none', border: 'none',
                color: EARTH.stone[400], fontSize: '13px', cursor: 'pointer', fontFamily: TYPOGRAPHY.body,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* License gate — shown when trial expired and user isn't licensed */}
      {trialExpired && <TrialExpiredGate onLicenseActivated={onLicenseActivated} />}

      {/* Self-service license management — deactivate this device to free a slot */}
      {showManageLicense && (
        <ManageLicense
          onClose={() => setShowManageLicense(false)}
          onDeactivated={() => window.location.reload()}
        />
      )}
    </div>
  );
}

export default function arrangementLab() {
  const params = new URLSearchParams(window.location.search);
  const skipLanding = params.get('demo') === 'true';

  // Dev-only param: ?reset_trial=true clears the trial clock.
  // Gated to dev builds so it can't be used as a production trial-reset backdoor.
  if (import.meta.env.DEV && params.get('reset_trial') === 'true') {
    localStorage.removeItem(TRIAL_KEY);
    window.location.href = window.location.pathname;
    return null;
  }

  const activateParam = new URLSearchParams(window.location.search).get('activate') === '1';
  // Licensed users skip StudioLandingPage entirely — go straight to the app/welcome view
  const [view, setView] = useState((skipLanding || isLicensed()) ? 'app' : 'landing');
  const [importedTemplate, setImportedTemplate] = useState(null);
  // Don't show activate dialog if already licensed
  const [showLandingLicenseDialog, setShowLandingLicenseDialog] = useState(activateParam && !isLicensed());
  const [showEmailGate, setShowEmailGate] = useState(false);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [trialInfo, setTrialInfo] = useState(() => getTrialInfo());
  const [licensed, setLicensed] = useState(() => isLicensed());

  const handleLicenseActivated = () => {
    setLicensed(true);
    setShowLandingLicenseDialog(false);
    // Brief pause so user sees the "License activated — welcome!" success state
    // in LicenseKeyInput before being routed into the app
    setTimeout(() => {
      setImportedTemplate(null);
      setView('app');
    }, 1200);
  };

  const handleEmailSubmitted = (email, name) => {
    saveTrialUser(email, name);
    startTrial();
    setTrialInfo(getTrialInfo());
    setShowEmailGate(false);
    const validTemplate = pendingTemplate && pendingTemplate.sections && Array.isArray(pendingTemplate.sections) ? pendingTemplate : null;
    setImportedTemplate(validTemplate);
    setPendingTemplate(null);
    setView('app');
  };

  const handleStartApp = (template = null) => {
    if (template === 'enter-license') {
      setShowLandingLicenseDialog(true);
      return;
    }
    // Only show email gate if unlicensed AND trial hasn't started AND no email on file
    if (!licensed && !getTrialEmail() && !trialInfo.hasStarted) {
      setPendingTemplate(template);
      setShowEmailGate(true);
      return;
    }
    const validTemplate = template && template.sections && Array.isArray(template.sections) ? template : null;
    setImportedTemplate(validTemplate);
    setView('app');
  };

  const handleViewProjects = () => {
    alert('Project library coming soon! Currently, projects are session-based.');
  };

  return view === 'app' ? (
    <>
      <StudioApp
        onBackToHome={() => { window.location.href = '/'; }}
        initialTemplate={importedTemplate}
        trialDaysLeft={licensed ? undefined : trialInfo.daysLeft}
        trialExpired={!licensed && trialInfo.expired && !skipLanding}
        onLicenseActivated={handleLicenseActivated}
      />
    </>
  ) : (
    <>
      <StudioLandingPage
        onStartApp={handleStartApp}
        onViewProjects={handleViewProjects}
        trialExpired={!licensed && trialInfo.expired}
        licensed={licensed}
      />
      {showLandingLicenseDialog && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(26,26,26,0.6)', backdropFilter: 'blur(6px)',
        }} onClick={() => setShowLandingLicenseDialog(false)}>
          <div style={{
            backgroundColor: EARTH.cream, borderRadius: '16px', padding: '32px',
            maxWidth: '440px', width: '90%',
            boxShadow: '0 25px 50px rgba(0,0,0,0.2)',
            border: `1px solid ${EARTH.stone[200]}`,
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ fontFamily: TYPOGRAPHY.display, fontSize: '22px', color: EARTH.ink, marginBottom: '8px', textTransform: 'lowercase' }}>
              activate license
            </h3>
            <p style={{ color: EARTH.stone[500], fontSize: '13px', marginBottom: '20px', fontFamily: TYPOGRAPHY.body }}>
              Enter the license key from your purchase confirmation email.
            </p>
            <LicenseKeyInput onActivated={handleLicenseActivated} />
            <button
              onClick={() => setShowLandingLicenseDialog(false)}
              style={{
                display: 'block', margin: '16px auto 0', background: 'none', border: 'none',
                color: EARTH.stone[400], fontSize: '13px', cursor: 'pointer', fontFamily: TYPOGRAPHY.body,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {showEmailGate && (
        <TrialEmailGate
          onSubmit={handleEmailSubmitted}
          onCancel={() => { setShowEmailGate(false); setPendingTemplate(null); }}
        />
      )}
    </>
  );
}
